/**
 * Canonical route registry.
 *
 * P2-001: Single authority for declarative route entries.
 * P2-002: Route contracts define stable product identity.
 *           Availability is determined by backend navigation.
 * P2-009: Protected routes carry required operation IDs.
 * W7: Remove unsafe ID casts.
 */
import { ROUTE_IDS, ICON_IDS, MODULE_KEYS, FEATURE_KEYS, TELEMETRY_IDS, I18N_KEYS, OPERATION_IDS, } from './ids.js';
export const canonicalRouteRegistry = [
    // Auth routes (public)
    {
        id: 'route-login',
        schemaVersion: 1,
        module: MODULE_KEYS.AUTH,
        feature: FEATURE_KEYS.AUTH_LOGIN,
        telemetryId: TELEMETRY_IDS.AUTH_LOGIN,
        accessibilityLabelKey: I18N_KEYS.AUTH_LOGIN,
        privacyClassification: 'PUBLIC',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.LOGIN,
        path: '/login',
        pageId: 'page-login',
        layoutId: 'auth',
        authentication: 'public',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.AUTH_LOGIN],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.AUTH_LOGIN,
    },
    {
        id: 'route-forgot-password',
        schemaVersion: 1,
        module: MODULE_KEYS.AUTH,
        feature: FEATURE_KEYS.AUTH_FORGOT_PASSWORD,
        telemetryId: TELEMETRY_IDS.AUTH_FORGOT_PASSWORD,
        accessibilityLabelKey: I18N_KEYS.AUTH_FORGOT_PASSWORD,
        privacyClassification: 'PUBLIC',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.FORGOT_PASSWORD,
        path: '/forgot-password',
        pageId: 'page-forgot-password',
        layoutId: 'auth',
        authentication: 'public',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.AUTH_FORGOT_PASSWORD],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.AUTH_FORGOT_PASSWORD,
    },
    {
        id: 'route-reset-password',
        schemaVersion: 1,
        module: MODULE_KEYS.AUTH,
        feature: FEATURE_KEYS.AUTH_RESET_PASSWORD,
        telemetryId: TELEMETRY_IDS.AUTH_RESET_PASSWORD,
        accessibilityLabelKey: I18N_KEYS.AUTH_RESET_PASSWORD,
        privacyClassification: 'PUBLIC',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.RESET_PASSWORD,
        path: '/reset-password',
        pageId: 'page-reset-password',
        layoutId: 'auth',
        authentication: 'public',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.AUTH_RESET_PASSWORD],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.AUTH_RESET_PASSWORD,
    },
    {
        id: 'route-mfa-verify',
        schemaVersion: 1,
        module: MODULE_KEYS.AUTH,
        feature: FEATURE_KEYS.AUTH_MFA_VERIFY,
        telemetryId: TELEMETRY_IDS.AUTH_MFA_VERIFY,
        accessibilityLabelKey: I18N_KEYS.AUTH_MFA_VERIFY,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'SUCCESS_ONLY',
        owner: 'platform',
        routeId: ROUTE_IDS.MFA_VERIFY,
        path: '/mfa/verify',
        pageId: 'page-mfa-verify',
        layoutId: 'auth',
        authentication: 'public',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.AUTH_MFA_VERIFY],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.AUTH_MFA_VERIFY,
    },
    {
        id: 'route-initial-password-change',
        schemaVersion: 1,
        module: MODULE_KEYS.AUTH,
        feature: FEATURE_KEYS.AUTH_MFA_VERIFY,
        telemetryId: TELEMETRY_IDS.AUTH_MFA_VERIFY,
        accessibilityLabelKey: I18N_KEYS.AUTH_MFA_VERIFY,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'SUCCESS_ONLY',
        owner: 'platform',
        routeId: ROUTE_IDS.INITIAL_PASSWORD_CHANGE,
        path: '/me/password/initial',
        pageId: 'page-initial-password-change',
        layoutId: 'auth',
        authentication: 'public',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.AUTH_MFA_VERIFY],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.AUTH_MFA_VERIFY,
    },
    {
        id: 'route-mfa-enroll',
        schemaVersion: 1,
        module: MODULE_KEYS.AUTH,
        feature: FEATURE_KEYS.AUTH_MFA_ENROLL,
        telemetryId: TELEMETRY_IDS.AUTH_MFA_ENROLL,
        accessibilityLabelKey: I18N_KEYS.AUTH_MFA_ENROLL,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'SUCCESS_ONLY',
        owner: 'platform',
        routeId: ROUTE_IDS.MFA_ENROLL,
        path: '/mfa/enroll',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'SUPPORT_DELEGATE'],
        pageId: 'page-mfa-enroll',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.AUTH_MFA_ENROLL],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.AUTH_MFA_ENROLL,
        operationId: OPERATION_IDS.AUTH_ENROLL_MFA,
    },
    {
        id: 'route-mfa-step-up',
        schemaVersion: 1,
        module: MODULE_KEYS.AUTH,
        feature: FEATURE_KEYS.AUTH_MFA_ENROLL,
        telemetryId: TELEMETRY_IDS.AUTH_MFA_ENROLL,
        accessibilityLabelKey: I18N_KEYS.AUTH_MFA_ENROLL,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'SUCCESS_ONLY',
        owner: 'platform',
        routeId: ROUTE_IDS.MFA_STEP_UP,
        path: '/mfa/step-up',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'SUPPORT_DELEGATE'],
        pageId: 'page-mfa-step-up',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.AUTH_MFA_ENROLL],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.AUTH_MFA_ENROLL,
        operationId: OPERATION_IDS.AUTH_ENROLL_MFA,
    },
    // App routes (authenticated)
    {
        id: 'route-dashboard',
        schemaVersion: 1,
        module: MODULE_KEYS.DASHBOARD,
        feature: FEATURE_KEYS.DASHBOARD_VIEW,
        telemetryId: TELEMETRY_IDS.DASHBOARD_VIEW,
        accessibilityLabelKey: I18N_KEYS.NAVIGATION_DASHBOARD,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'SUCCESS_ONLY',
        owner: 'platform',
        routeId: ROUTE_IDS.DASHBOARD,
        path: '/dashboard',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'ORG_CLIENT', 'SUPPORT_DELEGATE'],
        pageId: 'page-dashboard',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DASHBOARD],
        navigationPlacement: 'primary',
        iconId: ICON_IDS.DASHBOARD,
        titleKey: I18N_KEYS.NAVIGATION_DASHBOARD,
        operationId: OPERATION_IDS.DASHBOARD_VIEW,
    },
    {
        id: 'route-transactions',
        schemaVersion: 1,
        module: MODULE_KEYS.TRANSACTIONS,
        feature: FEATURE_KEYS.TRANSACTIONS_LIST,
        telemetryId: TELEMETRY_IDS.TRANSACTIONS_LIST,
        accessibilityLabelKey: I18N_KEYS.NAVIGATION_TRANSACTIONS,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.TRANSACTIONS,
        path: '/transactions',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'ORG_CLIENT'],
        pageId: 'page-transactions',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_TRANSACTIONS],
        navigationPlacement: 'hidden',
        iconId: ICON_IDS.TRANSACTIONS,
        titleKey: I18N_KEYS.NAVIGATION_TRANSACTIONS,
        operationId: OPERATION_IDS.TRANSACTION_LIST,
    },
    {
        id: 'route-transaction-create',
        schemaVersion: 1,
        module: MODULE_KEYS.TRANSACTIONS,
        feature: FEATURE_KEYS.TRANSACTIONS_CREATE,
        telemetryId: TELEMETRY_IDS.TRANSACTIONS_CREATE,
        accessibilityLabelKey: I18N_KEYS.TRANSACTION_CREATE,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.TRANSACTION_CREATE,
        path: '/transactions/create',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER'],
        pageId: 'page-transaction-form',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_TRANSACTIONS, I18N_KEYS.TRANSACTION_CREATE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.TRANSACTION_CREATE,
        operationId: OPERATION_IDS.TRANSACTION_CREATE,
    },
    {
        id: 'route-transaction-detail',
        schemaVersion: 1,
        module: MODULE_KEYS.TRANSACTIONS,
        feature: FEATURE_KEYS.TRANSACTIONS_DETAIL,
        telemetryId: TELEMETRY_IDS.TRANSACTIONS_DETAIL,
        accessibilityLabelKey: I18N_KEYS.TRANSACTION_DETAIL,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.TRANSACTION_DETAIL,
        path: '/transactions/:id',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER'],
        pageId: 'page-transaction-detail',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_TRANSACTIONS, I18N_KEYS.TRANSACTION_DETAIL],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.TRANSACTION_DETAIL,
        operationId: OPERATION_IDS.TRANSACTION_GET,
    },
    {
        id: 'route-transaction-approvals',
        schemaVersion: 1,
        module: MODULE_KEYS.TRANSACTIONS,
        feature: FEATURE_KEYS.TRANSACTIONS_APPROVALS,
        telemetryId: TELEMETRY_IDS.TRANSACTIONS_APPROVALS,
        accessibilityLabelKey: I18N_KEYS.TRANSACTION_APPROVALS,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.TRANSACTION_APPROVALS,
        path: '/transactions/approvals',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER'],
        pageId: 'page-transaction-approvals',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_TRANSACTIONS, I18N_KEYS.TRANSACTION_APPROVALS],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.TRANSACTION_APPROVALS,
        operationId: OPERATION_IDS.TRANSACTION_APPROVE,
    },
    {
        id: 'route-transaction-reversals',
        schemaVersion: 1,
        module: MODULE_KEYS.TRANSACTIONS,
        feature: FEATURE_KEYS.TRANSACTIONS_REVERSALS,
        telemetryId: TELEMETRY_IDS.TRANSACTIONS_REVERSALS,
        accessibilityLabelKey: I18N_KEYS.TRANSACTION_REVERSALS,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.TRANSACTION_REVERSALS,
        path: '/transactions/reversals',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER'],
        pageId: 'page-transaction-reversals',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_TRANSACTIONS, I18N_KEYS.TRANSACTION_REVERSALS],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.TRANSACTION_REVERSALS,
        operationId: OPERATION_IDS.TRANSACTION_REVERSE,
    },
    {
        id: 'route-settings',
        schemaVersion: 1,
        module: MODULE_KEYS.SETTINGS,
        feature: FEATURE_KEYS.SETTINGS_VIEW,
        telemetryId: TELEMETRY_IDS.SETTINGS_VIEW,
        accessibilityLabelKey: I18N_KEYS.NAVIGATION_SETTINGS,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'SUCCESS_ONLY',
        owner: 'platform',
        routeId: ROUTE_IDS.SETTINGS,
        path: '/settings',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'ORG_CLIENT', 'SUPPORT_DELEGATE'],
        pageId: 'page-settings',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_SETTINGS],
        navigationPlacement: 'settings',
        iconId: ICON_IDS.SETTINGS,
        titleKey: I18N_KEYS.NAVIGATION_SETTINGS,
        operationId: OPERATION_IDS.SETTINGS_VIEW,
    },
    {
        id: 'route-settings-profile',
        schemaVersion: 1,
        module: MODULE_KEYS.SETTINGS,
        feature: FEATURE_KEYS.SETTINGS_PROFILE,
        telemetryId: TELEMETRY_IDS.SETTINGS_PROFILE,
        accessibilityLabelKey: I18N_KEYS.SETTINGS_PROFILE,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'SUCCESS_ONLY',
        owner: 'platform',
        routeId: ROUTE_IDS.SETTINGS_PROFILE,
        path: '/settings/profile',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'SUPPORT_DELEGATE'],
        pageId: 'page-settings-profile',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_SETTINGS, I18N_KEYS.SETTINGS_PROFILE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.SETTINGS_PROFILE,
        iconId: ICON_IDS.PERSON,
        operationId: OPERATION_IDS.SETTINGS_PROFILE,
        avatarLabelKey: I18N_KEYS.AVATAR_PROFILE_PREFERENCES,
        avatarDescriptionKey: I18N_KEYS.AVATAR_PROFILE_PREFERENCES_DESCRIPTION,
    },
    {
        id: 'route-settings-preferences',
        schemaVersion: 1,
        module: MODULE_KEYS.SETTINGS,
        feature: FEATURE_KEYS.SETTINGS_PREFERENCES,
        telemetryId: TELEMETRY_IDS.SETTINGS_PREFERENCES,
        accessibilityLabelKey: I18N_KEYS.SETTINGS_PREFERENCES,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.SETTINGS_PREFERENCES,
        path: '/settings/preferences',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER'],
        pageId: 'page-settings-preferences',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_SETTINGS, I18N_KEYS.SETTINGS_PREFERENCES],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.SETTINGS_PREFERENCES,
        iconId: ICON_IDS.TUNE,
        operationId: OPERATION_IDS.SETTINGS_PREFERENCES,
    },
    {
        id: 'route-settings-mfa',
        schemaVersion: 1,
        module: MODULE_KEYS.SETTINGS,
        feature: FEATURE_KEYS.SETTINGS_MFA,
        telemetryId: TELEMETRY_IDS.SETTINGS_MFA,
        accessibilityLabelKey: I18N_KEYS.SETTINGS_MFA,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'SUCCESS_ONLY',
        owner: 'platform',
        routeId: ROUTE_IDS.SETTINGS_MFA,
        path: '/settings/mfa',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'SUPPORT_DELEGATE'],
        pageId: 'page-settings-mfa',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_SETTINGS, I18N_KEYS.SETTINGS_MFA],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.SETTINGS_MFA,
        iconId: ICON_IDS.SECURITY,
        operationId: OPERATION_IDS.SETTINGS_MFA,
    },
    {
        id: 'route-settings-password',
        schemaVersion: 1,
        module: MODULE_KEYS.SETTINGS,
        feature: FEATURE_KEYS.SETTINGS_PASSWORD,
        telemetryId: TELEMETRY_IDS.SETTINGS_PASSWORD,
        accessibilityLabelKey: I18N_KEYS.SETTINGS_PASSWORD,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'SUCCESS_ONLY',
        owner: 'platform',
        routeId: ROUTE_IDS.SETTINGS_PASSWORD,
        path: '/settings/password',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'SUPPORT_DELEGATE'],
        pageId: 'page-settings-password',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_SETTINGS, I18N_KEYS.SETTINGS_PASSWORD],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.SETTINGS_PASSWORD,
        iconId: ICON_IDS.KEY,
        operationId: OPERATION_IDS.AUTH_PASSWORD_CHANGE,
    },
    {
        id: 'route-settings-sessions',
        schemaVersion: 1,
        module: MODULE_KEYS.SETTINGS,
        feature: FEATURE_KEYS.SETTINGS_SESSIONS,
        telemetryId: TELEMETRY_IDS.SETTINGS_SESSIONS,
        accessibilityLabelKey: I18N_KEYS.SETTINGS_SESSIONS,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'SUCCESS_ONLY',
        owner: 'platform',
        routeId: ROUTE_IDS.SETTINGS_SESSIONS,
        path: '/settings/sessions',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'SUPPORT_DELEGATE'],
        pageId: 'page-settings-sessions',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_SETTINGS, I18N_KEYS.SETTINGS_SESSIONS],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.SETTINGS_SESSIONS,
        iconId: ICON_IDS.SECURITY,
        operationId: OPERATION_IDS.AUTH_SESSION_MANAGE,
    },
    {
        id: 'route-settings-security',
        schemaVersion: 1,
        module: MODULE_KEYS.SETTINGS,
        feature: FEATURE_KEYS.SETTINGS_SECURITY,
        telemetryId: TELEMETRY_IDS.SETTINGS_SECURITY,
        accessibilityLabelKey: I18N_KEYS.SETTINGS_SECURITY,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'SUCCESS_ONLY',
        owner: 'platform',
        routeId: ROUTE_IDS.SETTINGS_SECURITY,
        path: '/settings/security',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'SUPPORT_DELEGATE'],
        pageId: 'page-settings-security',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_SETTINGS, I18N_KEYS.SETTINGS_SECURITY],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.SETTINGS_SECURITY,
        iconId: ICON_IDS.SECURITY,
        operationId: OPERATION_IDS.SETTINGS_SECURITY,
        avatarLabelKey: I18N_KEYS.AVATAR_SECURITY,
        avatarDescriptionKey: I18N_KEYS.AVATAR_SECURITY_DESCRIPTION,
    },
    // Planned routes with backend capability pending
    {
        id: 'route-holdings',
        schemaVersion: 1,
        module: MODULE_KEYS.HOLDINGS,
        feature: FEATURE_KEYS.HOLDINGS_VIEW,
        telemetryId: TELEMETRY_IDS.HOLDINGS_VIEW,
        accessibilityLabelKey: I18N_KEYS.NAVIGATION_HOLDINGS,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.HOLDINGS,
        path: '/holdings',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'SUPPORT_DELEGATE'],
        pageId: 'page-holdings',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_HOLDINGS],
        navigationPlacement: 'hidden',
        iconId: ICON_IDS.HOLDINGS,
        titleKey: I18N_KEYS.NAVIGATION_HOLDINGS,
        operationId: OPERATION_IDS.HOLDINGS_VIEW,
    },
    {
        id: 'route-reports',
        schemaVersion: 1,
        module: MODULE_KEYS.REPORTS,
        feature: FEATURE_KEYS.REPORTS_VIEW,
        telemetryId: TELEMETRY_IDS.REPORTS_VIEW,
        accessibilityLabelKey: I18N_KEYS.NAVIGATION_REPORTS,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        routeId: ROUTE_IDS.REPORTS,
        path: '/reports',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'ORG_CLIENT', 'SUPPORT_DELEGATE'],
        pageId: 'page-reports',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'PLANNED',
        disabledReasonKey: I18N_KEYS.REPORTS_PLANNED,
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_REPORTS],
        navigationPlacement: 'hidden',
        iconId: ICON_IDS.REPORTS,
        titleKey: I18N_KEYS.NAVIGATION_REPORTS,
        operationId: OPERATION_IDS.REPORTS_VIEW,
    },
    {
        id: 'route-report-detail',
        schemaVersion: 1,
        module: MODULE_KEYS.REPORTS,
        feature: FEATURE_KEYS.REPORT_DETAIL_READ,
        telemetryId: TELEMETRY_IDS.REPORT_DETAIL_READ,
        accessibilityLabelKey: I18N_KEYS.PAGE_REPORT_DETAIL_TITLE,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        routeId: ROUTE_IDS.REPORT_DETAIL,
        path: '/reports/:reportId',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'ORG_CLIENT', 'SUPPORT_DELEGATE'],
        pageId: 'page-report-detail',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_REPORTS, I18N_KEYS.PAGE_REPORT_DETAIL_TITLE],
        navigationPlacement: 'hidden',
        iconId: ICON_IDS.REPORTS,
        titleKey: I18N_KEYS.PAGE_REPORT_DETAIL_TITLE,
        operationId: OPERATION_IDS.REPORT_DETAIL_READ,
    },
    {
        id: 'route-reconciliation',
        schemaVersion: 1,
        module: MODULE_KEYS.RECONCILIATION,
        feature: FEATURE_KEYS.RECONCILIATION_VIEW,
        telemetryId: TELEMETRY_IDS.RECONCILIATION_VIEW,
        accessibilityLabelKey: I18N_KEYS.NAVIGATION_RECONCILIATION,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.RECONCILIATION,
        path: '/reconciliation',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'ORG_CLIENT', 'SUPPORT_DELEGATE'],
        pageId: 'page-reconciliation',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'PLANNED',
        disabledReasonKey: I18N_KEYS.RECONCILIATION_PLANNED,
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_RECONCILIATION],
        navigationPlacement: 'hidden',
        iconId: ICON_IDS.RECONCILIATION,
        titleKey: I18N_KEYS.NAVIGATION_RECONCILIATION,
        operationId: OPERATION_IDS.RECONCILIATION_VIEW,
    },
    {
        id: 'route-corporate-actions',
        schemaVersion: 1,
        module: MODULE_KEYS.CORPORATE_ACTIONS,
        feature: FEATURE_KEYS.CORPORATE_ACTIONS_VIEW,
        telemetryId: TELEMETRY_IDS.CORPORATE_ACTIONS_VIEW,
        accessibilityLabelKey: I18N_KEYS.NAVIGATION_CORPORATE_ACTIONS,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.CORPORATE_ACTIONS,
        path: '/corporate-actions',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'ORG_CLIENT', 'SUPPORT_DELEGATE'],
        pageId: 'page-corporate-actions',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'PLANNED',
        disabledReasonKey: I18N_KEYS.CORPORATE_ACTIONS_PLANNED,
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_CORPORATE_ACTIONS],
        navigationPlacement: 'hidden',
        iconId: ICON_IDS.CORPORATE_ACTIONS,
        titleKey: I18N_KEYS.NAVIGATION_CORPORATE_ACTIONS,
        operationId: OPERATION_IDS.CORPORATE_ACTIONS_VIEW,
    },
    // Admin hub
    {
        id: 'route-admin-hub',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_HUB,
        telemetryId: TELEMETRY_IDS.ADMIN_HUB,
        accessibilityLabelKey: I18N_KEYS.HUB_ADMIN_ACCESSIBILITY,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.ADMIN_HUB,
        path: '/admin',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'ORG_CLIENT', 'SUPPORT_DELEGATE'],
        pageId: 'page-admin-hub',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.HUB_ADMIN_TITLE,
        operationId: OPERATION_IDS.ADMIN_HUB,
    },
    {
        id: 'route-platform-admin-hub',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_HUB,
        telemetryId: TELEMETRY_IDS.ADMIN_HUB,
        accessibilityLabelKey: I18N_KEYS.HUB_ADMIN_ACCESSIBILITY,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.PLATFORM_ADMIN_HUB,
        path: '/platform/admin',
        allowedPersonas: ['PLATFORM_ROOT', 'PLATFORM_ADMIN'],
        pageId: 'page-platform-admin-hub',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.HUB_ADMIN_TITLE,
        operationId: OPERATION_IDS.ADMIN_HUB_PLATFORM,
    },
    {
        id: 'route-organization-admin-hub',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_HUB,
        telemetryId: TELEMETRY_IDS.ADMIN_HUB,
        accessibilityLabelKey: I18N_KEYS.HUB_ADMIN_ACCESSIBILITY,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.ORGANIZATION_ADMIN_HUB,
        path: '/organization/admin',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-organization-admin-hub',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.HUB_ADMIN_TITLE,
        operationId: OPERATION_IDS.ADMIN_HUB_ORGANIZATION,
    },
    {
        id: 'route-organization-governance-workspace',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION_GOVERNANCE,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION_GOVERNANCE,
        accessibilityLabelKey: I18N_KEYS.PAGE_ORGANIZATION_GOVERNANCE_TITLE,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        routeId: ROUTE_IDS.ORGANIZATION_GOVERNANCE_WORKSPACE,
        path: '/organization/governance',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-organization-governance-workspace',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_ORGANIZATION_GOVERNANCE_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_ORGANIZATION_GOVERNANCE_TITLE,
        operationId: OPERATION_IDS.ORGANIZATION_GOVERNANCE_WORKSPACE_READ,
    },
    {
        id: 'route-users',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_USERS,
        telemetryId: TELEMETRY_IDS.ADMIN_USERS,
        accessibilityLabelKey: I18N_KEYS.PAGE_USERS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.USERS,
        path: '/admin/users',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-users',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_USERS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_USERS_TITLE,
        operationId: OPERATION_IDS.ADMIN_USERS_VIEW,
    },
    {
        id: 'route-roles',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ROLES,
        telemetryId: TELEMETRY_IDS.ADMIN_ROLES,
        accessibilityLabelKey: I18N_KEYS.PAGE_ROLES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.ROLES,
        path: '/admin/roles',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-roles',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'PLANNED',
        disabledReasonKey: I18N_KEYS.COMMON_PLANNED,
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_ROLES_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_ROLES_TITLE,
        operationId: OPERATION_IDS.ADMIN_ROLES_VIEW,
    },
    {
        id: 'route-organization-profile',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION,
        accessibilityLabelKey: I18N_KEYS.PAGE_ORGANIZATION_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.ORGANIZATION_PROFILE,
        path: '/admin/organizations',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-organization-profile',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_ORGANIZATION_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_ORGANIZATION_TITLE,
        operationId: OPERATION_IDS.ORGANIZATION_PROFILE_READ,
    },
    {
        id: 'route-organization-profile-detail',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION_DETAIL,
        accessibilityLabelKey: I18N_KEYS.PAGE_ORGANIZATION_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.ORGANIZATION_PROFILE_DETAIL,
        path: '/admin/organizations/:id',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-organization-profile-detail',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_ORGANIZATION_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_ORGANIZATION_DETAIL_TITLE,
        operationId: OPERATION_IDS.ORGANIZATION_PROFILE_DETAIL_READ,
    },
    {
        id: 'route-organization-profile-edit',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION_DETAIL,
        accessibilityLabelKey: I18N_KEYS.PAGE_ORGANIZATION_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        routeId: ROUTE_IDS.ORGANIZATION_PROFILE_EDIT,
        path: '/admin/organizations/:id/edit',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-organization-profile-edit',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_ORGANIZATION_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_ORGANIZATION_TITLE,
        operationId: OPERATION_IDS.ORGANIZATION_PROFILE_UPDATE,
    },
    {
        id: 'route-audit-explorer',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_AUDIT,
        telemetryId: TELEMETRY_IDS.ADMIN_AUDIT,
        accessibilityLabelKey: I18N_KEYS.PAGE_AUDIT_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.AUDIT_EXPLORER,
        path: '/admin/audit',
        allowedPersonas: ['ORG_ADMIN', 'SUPPORT_DELEGATE'],
        pageId: 'page-audit-explorer',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_AUDIT_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_AUDIT_TITLE,
        operationId: OPERATION_IDS.ADMIN_AUDIT_VIEW,
    },
    {
        id: 'route-platform-access-inspector',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_PLATFORM_ACCESS_INSPECTOR,
        telemetryId: TELEMETRY_IDS.ADMIN_PLATFORM_ACCESS_INSPECTOR,
        accessibilityLabelKey: I18N_KEYS.PAGE_PLATFORM_ACCESS_INSPECTOR_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.PLATFORM_ACCESS_INSPECTOR,
        path: '/admin/platform-access-inspector',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-platform-access-inspector',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_PLATFORM_ACCESS_INSPECTOR_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_PLATFORM_ACCESS_INSPECTOR_TITLE,
        operationId: OPERATION_IDS.ACCESS_READ_PERMISSIONS,
    },
    {
        id: 'route-organization-access-inspector',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION_ACCESS_INSPECTOR,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION_ACCESS_INSPECTOR,
        accessibilityLabelKey: I18N_KEYS.PAGE_ORGANIZATION_ACCESS_INSPECTOR_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.ORGANIZATION_ACCESS_INSPECTOR,
        path: '/admin/organization-access-inspector',
        allowedPersonas: ['ORG_ADMIN', 'SUPPORT_DELEGATE'],
        pageId: 'page-organization-access-inspector',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [
            I18N_KEYS.NAVIGATION_ADMIN,
            I18N_KEYS.PAGE_ORGANIZATION_ACCESS_INSPECTOR_TITLE,
        ],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_ORGANIZATION_ACCESS_INSPECTOR_TITLE,
        operationId: OPERATION_IDS.ORGANIZATION_ACCESS_INSPECTOR_READ,
    },
    {
        id: 'route-users-create',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_USERS,
        telemetryId: TELEMETRY_IDS.ADMIN_USERS_CREATE,
        accessibilityLabelKey: I18N_KEYS.PAGE_USERS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.USERS_CREATE,
        path: '/admin/users/new',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-users-create',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_USERS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_USERS_CREATE_TITLE,
        operationId: OPERATION_IDS.ADMIN_USER_CREATE,
    },
    {
        id: 'route-users-detail',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_USERS,
        telemetryId: TELEMETRY_IDS.ADMIN_USERS_DETAIL,
        accessibilityLabelKey: I18N_KEYS.PAGE_USERS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.USERS_DETAIL,
        path: '/admin/users/:id',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-users-detail',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_USERS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_USERS_TITLE,
        operationId: OPERATION_IDS.ADMIN_USERS_DETAIL,
    },
    {
        id: 'route-users-edit',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_USERS,
        telemetryId: TELEMETRY_IDS.ADMIN_USERS_EDIT,
        accessibilityLabelKey: I18N_KEYS.PAGE_USERS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.USERS_EDIT,
        path: '/admin/users/:id/edit',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-users-edit',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_USERS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_USERS_EDIT_TITLE,
        operationId: OPERATION_IDS.ADMIN_USER_UPDATE,
    },
    {
        id: 'route-roles-create',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ROLES,
        telemetryId: TELEMETRY_IDS.ADMIN_ROLES_CREATE,
        accessibilityLabelKey: I18N_KEYS.PAGE_ROLES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.ROLES_CREATE,
        path: '/admin/roles/new',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-roles-create',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_ROLES_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_ROLES_CREATE_TITLE,
        operationId: OPERATION_IDS.ADMIN_ROLE_CREATE,
    },
    {
        id: 'route-roles-detail',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ROLES,
        telemetryId: TELEMETRY_IDS.ADMIN_ROLES_DETAIL,
        accessibilityLabelKey: I18N_KEYS.PAGE_ROLES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.ROLES_DETAIL,
        path: '/admin/roles/:id',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-roles-detail',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_ROLES_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_ROLES_TITLE,
        operationId: OPERATION_IDS.ADMIN_ROLES_DETAIL,
    },
    {
        id: 'route-roles-edit',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ROLES,
        telemetryId: TELEMETRY_IDS.ADMIN_ROLES_EDIT,
        accessibilityLabelKey: I18N_KEYS.PAGE_ROLES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.ROLES_EDIT,
        path: '/admin/roles/:id/edit',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-roles-edit',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_ROLES_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_ROLES_EDIT_TITLE,
        operationId: OPERATION_IDS.ADMIN_ROLE_UPDATE,
    },
    // Dedicated platform administration resources
    {
        id: 'route-platform-organizations-list',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION,
        accessibilityLabelKey: I18N_KEYS.PAGE_PLATFORM_ORGANIZATIONS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.PLATFORM_ORGANIZATIONS_LIST,
        path: '/platform/organizations',
        allowedPersonas: ['PLATFORM_ROOT', 'PLATFORM_ADMIN'],
        pageId: 'page-platform-organizations-list',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_PLATFORM_ORGANIZATIONS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_PLATFORM_ORGANIZATIONS_TITLE,
        operationId: OPERATION_IDS.PLATFORM_ORGANIZATIONS_LIST,
    },
    {
        id: 'route-platform-organizations-create',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION,
        accessibilityLabelKey: I18N_KEYS.PAGE_PLATFORM_ORGANIZATIONS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.PLATFORM_ORGANIZATIONS_CREATE,
        path: '/platform/organizations/new',
        allowedPersonas: ['PLATFORM_ROOT', 'PLATFORM_ADMIN'],
        pageId: 'page-platform-organizations-create',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_PLATFORM_ORGANIZATIONS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_PLATFORM_ORGANIZATIONS_TITLE,
        operationId: OPERATION_IDS.PLATFORM_ORGANIZATIONS_CREATE,
    },
    {
        id: 'route-platform-organizations-detail',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION,
        accessibilityLabelKey: I18N_KEYS.PAGE_PLATFORM_ORGANIZATIONS_DETAIL_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.PLATFORM_ORGANIZATIONS_DETAIL,
        path: '/platform/organizations/:organizationId',
        allowedPersonas: ['PLATFORM_ROOT', 'PLATFORM_ADMIN'],
        pageId: 'page-platform-organizations-detail',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_PLATFORM_ORGANIZATIONS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_PLATFORM_ORGANIZATIONS_DETAIL_TITLE,
        operationId: OPERATION_IDS.PLATFORM_ORGANIZATIONS_READ,
    },
    {
        id: 'route-platform-organizations-edit',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION,
        accessibilityLabelKey: I18N_KEYS.PAGE_PLATFORM_ORGANIZATIONS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.PLATFORM_ORGANIZATIONS_EDIT,
        path: '/platform/organizations/:organizationId/edit',
        allowedPersonas: ['PLATFORM_ROOT', 'PLATFORM_ADMIN'],
        pageId: 'page-platform-organizations-edit',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_PLATFORM_ORGANIZATIONS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_PLATFORM_ORGANIZATIONS_TITLE,
        operationId: OPERATION_IDS.PLATFORM_ORGANIZATIONS_UPDATE,
    },
    {
        id: 'route-platform-modules-list',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ROLES,
        telemetryId: TELEMETRY_IDS.ADMIN_ROLES,
        accessibilityLabelKey: I18N_KEYS.PAGE_PLATFORM_MODULES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.PLATFORM_MODULES_LIST,
        path: '/platform/modules',
        allowedPersonas: ['PLATFORM_ROOT', 'PLATFORM_ADMIN'],
        pageId: 'page-platform-modules-list',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_PLATFORM_MODULES_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_PLATFORM_MODULES_TITLE,
        operationId: OPERATION_IDS.PLATFORM_MODULES_LIST,
    },
    {
        id: 'route-platform-modules-detail',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ROLES,
        telemetryId: TELEMETRY_IDS.ADMIN_ROLES,
        accessibilityLabelKey: I18N_KEYS.PAGE_PLATFORM_MODULES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.PLATFORM_MODULES_DETAIL,
        path: '/platform/modules/:moduleId',
        allowedPersonas: ['PLATFORM_ROOT', 'PLATFORM_ADMIN'],
        pageId: 'page-platform-modules-detail',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'PLANNED',
        disabledReasonKey: I18N_KEYS.COMMON_PLANNED,
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_PLATFORM_MODULES_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_PLATFORM_MODULES_TITLE,
        operationId: OPERATION_IDS.PLATFORM_MODULES_READ,
    },
    {
        id: 'route-platform-module-assignments',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ROLES,
        telemetryId: TELEMETRY_IDS.ADMIN_ROLES,
        accessibilityLabelKey: I18N_KEYS.PAGE_MODULE_ASSIGNMENTS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.PLATFORM_MODULE_ASSIGNMENTS,
        path: '/platform/module-assignments',
        allowedPersonas: ['PLATFORM_ROOT', 'PLATFORM_ADMIN'],
        pageId: 'page-platform-module-assignments',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'PLANNED',
        disabledReasonKey: I18N_KEYS.COMMON_PLANNED,
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_MODULE_ASSIGNMENTS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_MODULE_ASSIGNMENTS_TITLE,
        operationId: OPERATION_IDS.PLATFORM_MODULES_ASSIGN,
    },
    {
        id: 'route-platform-users-list',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_USERS,
        telemetryId: TELEMETRY_IDS.ADMIN_USERS,
        accessibilityLabelKey: I18N_KEYS.PAGE_PLATFORM_USERS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.PLATFORM_USERS_LIST,
        path: '/platform/users',
        allowedPersonas: ['PLATFORM_ROOT', 'PLATFORM_ADMIN'],
        pageId: 'page-platform-users-list',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_PLATFORM_USERS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_PLATFORM_USERS_TITLE,
        operationId: OPERATION_IDS.PLATFORM_USERS_LIST,
    },
    {
        id: 'route-platform-users-create',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_USERS,
        telemetryId: TELEMETRY_IDS.ADMIN_USERS,
        accessibilityLabelKey: I18N_KEYS.PAGE_PLATFORM_USERS_CREATE_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.PLATFORM_USERS_CREATE,
        path: '/platform/users/new',
        allowedPersonas: ['PLATFORM_ROOT', 'PLATFORM_ADMIN'],
        pageId: 'page-platform-users-create',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_PLATFORM_USERS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_PLATFORM_USERS_CREATE_TITLE,
        operationId: OPERATION_IDS.PLATFORM_USERS_CREATE,
    },
    {
        id: 'route-platform-users-detail',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_USERS,
        telemetryId: TELEMETRY_IDS.ADMIN_USERS,
        accessibilityLabelKey: I18N_KEYS.PAGE_PLATFORM_USERS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.PLATFORM_USERS_DETAIL,
        path: '/platform/users/:userId',
        allowedPersonas: ['PLATFORM_ROOT', 'PLATFORM_ADMIN'],
        pageId: 'page-platform-users-detail',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_PLATFORM_USERS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_PLATFORM_USERS_TITLE,
        operationId: OPERATION_IDS.PLATFORM_USERS_READ,
    },
    {
        id: 'route-platform-users-edit',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_USERS,
        telemetryId: TELEMETRY_IDS.ADMIN_USERS,
        accessibilityLabelKey: I18N_KEYS.PAGE_PLATFORM_USERS_EDIT_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.PLATFORM_USERS_EDIT,
        path: '/platform/users/:userId/edit',
        allowedPersonas: ['PLATFORM_ROOT', 'PLATFORM_ADMIN'],
        pageId: 'page-platform-users-edit',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_PLATFORM_USERS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_PLATFORM_USERS_EDIT_TITLE,
        operationId: OPERATION_IDS.PLATFORM_USERS_UPDATE,
    },
    {
        id: 'route-platform-health',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_HUB,
        telemetryId: TELEMETRY_IDS.PLATFORM_HEALTH_CHECK,
        accessibilityLabelKey: I18N_KEYS.PAGE_PLATFORM_HEALTH_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.PLATFORM_HEALTH,
        path: '/platform/health',
        allowedPersonas: ['PLATFORM_ROOT', 'PLATFORM_ADMIN'],
        pageId: 'page-platform-health',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_PLATFORM_HEALTH_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_PLATFORM_HEALTH_TITLE,
        operationId: OPERATION_IDS.PLATFORM_HEALTH_CHECK,
    },
    {
        id: 'route-platform-support-delegations-list',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_MAINTENANCE_DELEGATIONS,
        telemetryId: TELEMETRY_IDS.CARD_MAINTENANCE_DELEGATIONS,
        accessibilityLabelKey: I18N_KEYS.PAGE_PLATFORM_SUPPORT_DELEGATIONS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        routeId: ROUTE_IDS.PLATFORM_SUPPORT_DELEGATIONS_LIST,
        path: '/platform/support-delegations',
        allowedPersonas: ['PLATFORM_ROOT', 'PLATFORM_ADMIN'],
        pageId: 'page-platform-support-delegations-list',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_PLATFORM_SUPPORT_DELEGATIONS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_PLATFORM_SUPPORT_DELEGATIONS_TITLE,
        operationId: OPERATION_IDS.PLATFORM_SUPPORT_DELEGATIONS_LIST,
    },
    {
        id: 'route-platform-support-delegations-create',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_MAINTENANCE_DELEGATIONS,
        telemetryId: TELEMETRY_IDS.CARD_MAINTENANCE_DELEGATIONS,
        accessibilityLabelKey: I18N_KEYS.PAGE_PLATFORM_SUPPORT_DELEGATIONS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        routeId: ROUTE_IDS.PLATFORM_SUPPORT_DELEGATIONS_CREATE,
        path: '/platform/support-delegations/new',
        allowedPersonas: ['PLATFORM_ROOT', 'PLATFORM_ADMIN'],
        pageId: 'page-platform-support-delegations-create',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_PLATFORM_SUPPORT_DELEGATIONS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_PLATFORM_SUPPORT_DELEGATIONS_TITLE,
        operationId: OPERATION_IDS.PLATFORM_SUPPORT_DELEGATIONS_CREATE,
    },
    {
        id: 'route-platform-support-delegations-detail',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_MAINTENANCE_DELEGATIONS,
        telemetryId: TELEMETRY_IDS.CARD_MAINTENANCE_DELEGATIONS,
        accessibilityLabelKey: I18N_KEYS.PAGE_PLATFORM_SUPPORT_DELEGATIONS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        routeId: ROUTE_IDS.PLATFORM_SUPPORT_DELEGATIONS_DETAIL,
        path: '/platform/support-delegations/:delegationId',
        allowedPersonas: ['PLATFORM_ROOT', 'PLATFORM_ADMIN'],
        pageId: 'page-platform-support-delegations-detail',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_PLATFORM_SUPPORT_DELEGATIONS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_PLATFORM_SUPPORT_DELEGATIONS_TITLE,
        operationId: OPERATION_IDS.PLATFORM_SUPPORT_DELEGATIONS_READ,
    },
    {
        id: 'route-platform-support-delegations-revoke',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_MAINTENANCE_DELEGATIONS,
        telemetryId: TELEMETRY_IDS.CARD_MAINTENANCE_DELEGATIONS,
        accessibilityLabelKey: I18N_KEYS.PAGE_PLATFORM_SUPPORT_DELEGATIONS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        routeId: ROUTE_IDS.PLATFORM_SUPPORT_DELEGATIONS_REVOKE,
        path: '/platform/support-delegations/:delegationId/revoke',
        allowedPersonas: ['PLATFORM_ROOT', 'PLATFORM_ADMIN'],
        pageId: 'page-platform-support-delegations-revoke',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_PLATFORM_SUPPORT_DELEGATIONS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_PLATFORM_SUPPORT_DELEGATIONS_TITLE,
        operationId: OPERATION_IDS.PLATFORM_SUPPORT_DELEGATIONS_REVOKE,
    },
    {
        id: 'route-platform-audit-list',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_AUDIT,
        telemetryId: TELEMETRY_IDS.ADMIN_AUDIT,
        accessibilityLabelKey: I18N_KEYS.PAGE_PLATFORM_AUDIT_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.PLATFORM_AUDIT_LIST,
        path: '/platform/audit',
        allowedPersonas: ['PLATFORM_ROOT', 'PLATFORM_ADMIN'],
        pageId: 'page-platform-audit-list',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_PLATFORM_AUDIT_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_PLATFORM_AUDIT_TITLE,
        operationId: OPERATION_IDS.PLATFORM_AUDIT_LIST,
    },
    {
        id: 'route-platform-audit-detail',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_AUDIT,
        telemetryId: TELEMETRY_IDS.ADMIN_AUDIT,
        accessibilityLabelKey: I18N_KEYS.PAGE_PLATFORM_AUDIT_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.PLATFORM_AUDIT_DETAIL,
        path: '/platform/audit/:auditId',
        allowedPersonas: ['PLATFORM_ROOT', 'PLATFORM_ADMIN'],
        pageId: 'page-platform-audit-detail',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_PLATFORM_AUDIT_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_PLATFORM_AUDIT_TITLE,
        operationId: OPERATION_IDS.PLATFORM_AUDIT_READ,
    },
    // Dedicated organization administration resources
    {
        id: 'route-organization-branches-list',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION_BRANCHES,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION,
        accessibilityLabelKey: I18N_KEYS.PAGE_BRANCHES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.ORGANIZATION_BRANCHES_LIST,
        path: '/admin/branches',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-organization-branches-list',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_BRANCHES_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_BRANCHES_TITLE,
        operationId: OPERATION_IDS.ORGANIZATION_BRANCHES_LIST,
    },
    {
        id: 'route-organization-branches-create',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION_BRANCHES,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION,
        accessibilityLabelKey: I18N_KEYS.PAGE_BRANCHES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.ORGANIZATION_BRANCHES_CREATE,
        path: '/admin/branches/new',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-organization-branches-create',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_BRANCHES_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_BRANCHES_TITLE,
        operationId: OPERATION_IDS.ORGANIZATION_BRANCHES_CREATE,
    },
    {
        id: 'route-organization-branches-detail',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION_BRANCHES,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION,
        accessibilityLabelKey: I18N_KEYS.PAGE_BRANCHES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.ORGANIZATION_BRANCHES_DETAIL,
        path: '/admin/branches/:branchId',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-organization-branches-detail',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_BRANCHES_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_BRANCHES_TITLE,
        operationId: OPERATION_IDS.ORGANIZATION_BRANCHES_READ,
    },
    {
        id: 'route-organization-branches-edit',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION_BRANCHES,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION,
        accessibilityLabelKey: I18N_KEYS.PAGE_BRANCHES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.ORGANIZATION_BRANCHES_EDIT,
        path: '/admin/branches/:branchId/edit',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-organization-branches-edit',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_BRANCHES_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_BRANCHES_TITLE,
        operationId: OPERATION_IDS.ORGANIZATION_BRANCHES_UPDATE,
    },
    {
        id: 'route-organization-modules-list',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION_MODULES,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION,
        accessibilityLabelKey: I18N_KEYS.PAGE_MODULE_ASSIGNMENTS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.ORGANIZATION_MODULES_LIST,
        path: '/admin/modules',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-organization-modules-list',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_MODULE_ASSIGNMENTS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_MODULE_ASSIGNMENTS_TITLE,
        operationId: OPERATION_IDS.ORGANIZATION_MODULES_LIST_ASSIGNED,
    },
    {
        id: 'route-organization-modules-create',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION_MODULES,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION,
        accessibilityLabelKey: I18N_KEYS.PAGE_MODULE_ASSIGNMENTS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        routeId: ROUTE_IDS.ORGANIZATION_MODULES_CREATE,
        path: '/admin/modules/new',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-organization-modules-create',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_MODULE_ASSIGNMENTS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_MODULE_ASSIGNMENTS_TITLE,
        operationId: OPERATION_IDS.ORGANIZATION_MODULES_ENABLE,
    },
    {
        id: 'route-organization-modules-configure',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION_MODULES,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION,
        accessibilityLabelKey: I18N_KEYS.PAGE_MODULE_ASSIGNMENTS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.ORGANIZATION_MODULES_CONFIGURE,
        path: '/admin/modules/:moduleId/configure',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-organization-modules-configure',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_MODULE_ASSIGNMENTS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_MODULE_ASSIGNMENTS_TITLE,
        operationId: OPERATION_IDS.ORGANIZATION_MODULES_CONFIGURE,
    },
    {
        id: 'route-organization-users-list',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_USERS,
        telemetryId: TELEMETRY_IDS.ADMIN_USERS,
        accessibilityLabelKey: I18N_KEYS.PAGE_ORGANIZATION_USERS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.ORGANIZATION_USERS_LIST,
        path: '/admin/organization/users',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-organization-users-list',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_ORGANIZATION_USERS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_ORGANIZATION_USERS_TITLE,
        operationId: OPERATION_IDS.ORGANIZATION_USERS_LIST,
    },
    {
        id: 'route-organization-users-create',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_USERS,
        telemetryId: TELEMETRY_IDS.ADMIN_USERS,
        accessibilityLabelKey: I18N_KEYS.PAGE_ORGANIZATION_USERS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.ORGANIZATION_USERS_CREATE,
        path: '/admin/organization/users/new',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-organization-users-create',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_ORGANIZATION_USERS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_ORGANIZATION_USERS_TITLE,
        operationId: OPERATION_IDS.ORGANIZATION_USERS_CREATE,
    },
    {
        id: 'route-organization-users-detail',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_USERS,
        telemetryId: TELEMETRY_IDS.ADMIN_USERS,
        accessibilityLabelKey: I18N_KEYS.PAGE_ORGANIZATION_USERS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.ORGANIZATION_USERS_DETAIL,
        path: '/admin/organization/users/:userId',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-organization-users-detail',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_ORGANIZATION_USERS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_ORGANIZATION_USERS_TITLE,
        operationId: OPERATION_IDS.ORGANIZATION_USERS_READ,
    },
    {
        id: 'route-organization-users-edit',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_USERS,
        telemetryId: TELEMETRY_IDS.ADMIN_USERS,
        accessibilityLabelKey: I18N_KEYS.PAGE_ORGANIZATION_USERS_EDIT_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.ORGANIZATION_USERS_EDIT,
        path: '/admin/organization/users/:userId/edit',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-organization-users-edit',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_ORGANIZATION_USERS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_ORGANIZATION_USERS_EDIT_TITLE,
        operationId: OPERATION_IDS.ORGANIZATION_USERS_UPDATE,
    },
    {
        id: 'route-organization-roles-list',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ROLES,
        telemetryId: TELEMETRY_IDS.ADMIN_ROLES,
        accessibilityLabelKey: I18N_KEYS.PAGE_ORGANIZATION_ROLES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.ORGANIZATION_ROLES_LIST,
        path: '/admin/organization/roles',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-organization-roles-list',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_ORGANIZATION_ROLES_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_ORGANIZATION_ROLES_TITLE,
        operationId: OPERATION_IDS.ORGANIZATION_ROLES_LIST,
    },
    {
        id: 'route-organization-roles-create',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ROLES,
        telemetryId: TELEMETRY_IDS.ADMIN_ROLES,
        accessibilityLabelKey: I18N_KEYS.PAGE_ORGANIZATION_ROLES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.ORGANIZATION_ROLES_CREATE,
        path: '/admin/organization/roles/new',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-organization-roles-create',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_ORGANIZATION_ROLES_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_ORGANIZATION_ROLES_TITLE,
        operationId: OPERATION_IDS.ORGANIZATION_ROLES_CREATE,
    },
    {
        id: 'route-organization-roles-detail',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ROLES,
        telemetryId: TELEMETRY_IDS.ADMIN_ROLES,
        accessibilityLabelKey: I18N_KEYS.PAGE_ORGANIZATION_ROLES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.ORGANIZATION_ROLES_DETAIL,
        path: '/admin/organization/roles/:roleId',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-organization-roles-detail',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_ORGANIZATION_ROLES_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_ORGANIZATION_ROLES_TITLE,
        operationId: OPERATION_IDS.ORGANIZATION_ROLES_READ,
    },
    {
        id: 'route-organization-roles-edit',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ROLES,
        telemetryId: TELEMETRY_IDS.ADMIN_ROLES,
        accessibilityLabelKey: I18N_KEYS.PAGE_ORGANIZATION_ROLES_EDIT_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.ORGANIZATION_ROLES_EDIT,
        path: '/admin/organization/roles/:roleId/edit',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-organization-roles-edit',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_ORGANIZATION_ROLES_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_ORGANIZATION_ROLES_EDIT_TITLE,
        operationId: OPERATION_IDS.ORGANIZATION_ROLES_UPDATE,
    },
    {
        id: 'route-organization-permission-assignments-list',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ROLES,
        telemetryId: TELEMETRY_IDS.ADMIN_ROLES,
        accessibilityLabelKey: I18N_KEYS.PAGE_ORGANIZATION_PERMISSION_ASSIGNMENTS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.ORGANIZATION_PERMISSION_ASSIGNMENTS_LIST,
        path: '/admin/permission-assignments',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-organization-permission-assignments-list',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'PLANNED',
        disabledReasonKey: I18N_KEYS.COMMON_PLANNED,
        breadcrumbKeys: [
            I18N_KEYS.NAVIGATION_ADMIN,
            I18N_KEYS.PAGE_ORGANIZATION_PERMISSION_ASSIGNMENTS_TITLE,
        ],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_ORGANIZATION_PERMISSION_ASSIGNMENTS_TITLE,
        operationId: OPERATION_IDS.ORGANIZATION_PERMISSION_ASSIGNMENTS_LIST,
    },
    {
        id: 'route-organization-permission-assignments-edit',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ROLES,
        telemetryId: TELEMETRY_IDS.ADMIN_ROLES,
        accessibilityLabelKey: I18N_KEYS.PAGE_ORGANIZATION_PERMISSION_ASSIGNMENTS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.ORGANIZATION_PERMISSION_ASSIGNMENTS_EDIT,
        path: '/admin/permission-assignments/:roleId/edit',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-organization-permission-assignments-edit',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'PLANNED',
        disabledReasonKey: I18N_KEYS.COMMON_PLANNED,
        breadcrumbKeys: [
            I18N_KEYS.NAVIGATION_ADMIN,
            I18N_KEYS.PAGE_ORGANIZATION_PERMISSION_ASSIGNMENTS_TITLE,
        ],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_ORGANIZATION_PERMISSION_ASSIGNMENTS_TITLE,
        operationId: OPERATION_IDS.ORGANIZATION_PERMISSION_ASSIGNMENTS_EDIT,
    },
    {
        id: 'route-organization-clients-list',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION,
        accessibilityLabelKey: I18N_KEYS.PAGE_CLIENTS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.ORGANIZATION_CLIENTS_LIST,
        path: '/admin/clients',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-organization-clients-list',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_CLIENTS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_CLIENTS_TITLE,
        operationId: OPERATION_IDS.ORGANIZATION_CLIENTS_LIST,
    },
    {
        id: 'route-organization-clients-create',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION,
        accessibilityLabelKey: I18N_KEYS.PAGE_CLIENTS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.ORGANIZATION_CLIENTS_CREATE,
        path: '/admin/clients/new',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-organization-clients-create',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_CLIENTS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_CLIENTS_TITLE,
        operationId: OPERATION_IDS.ORGANIZATION_CLIENTS_CREATE,
    },
    {
        id: 'route-organization-clients-detail',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION,
        accessibilityLabelKey: I18N_KEYS.PAGE_CLIENTS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.ORGANIZATION_CLIENTS_DETAIL,
        path: '/admin/clients/:clientId',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-organization-clients-detail',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_CLIENTS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_CLIENTS_TITLE,
        operationId: OPERATION_IDS.ORGANIZATION_CLIENTS_READ,
    },
    {
        id: 'route-organization-clients-edit',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION,
        accessibilityLabelKey: I18N_KEYS.PAGE_CLIENTS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.ORGANIZATION_CLIENTS_EDIT,
        path: '/admin/clients/:clientId/edit',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-organization-clients-edit',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_CLIENTS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_CLIENTS_TITLE,
        operationId: OPERATION_IDS.ORGANIZATION_CLIENTS_UPDATE,
    },
    {
        id: 'route-organization-client-accounts-list',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION,
        accessibilityLabelKey: I18N_KEYS.PAGE_CLIENT_ACCOUNTS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.ORGANIZATION_CLIENT_ACCOUNTS_LIST,
        path: '/admin/client-accounts',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-organization-client-accounts-list',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_CLIENT_ACCOUNTS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_CLIENT_ACCOUNTS_TITLE,
        operationId: OPERATION_IDS.ORGANIZATION_CLIENT_ACCOUNTS_LIST,
    },
    {
        id: 'route-organization-client-accounts-create',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION,
        accessibilityLabelKey: I18N_KEYS.PAGE_CLIENT_ACCOUNTS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.ORGANIZATION_CLIENT_ACCOUNTS_CREATE,
        path: '/admin/client-accounts/new',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-organization-client-accounts-create',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_CLIENT_ACCOUNTS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_CLIENT_ACCOUNTS_TITLE,
        operationId: OPERATION_IDS.ORGANIZATION_CLIENT_ACCOUNTS_CREATE,
    },
    {
        id: 'route-organization-client-accounts-detail',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION,
        accessibilityLabelKey: I18N_KEYS.PAGE_CLIENT_ACCOUNTS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.ORGANIZATION_CLIENT_ACCOUNTS_DETAIL,
        path: '/admin/client-accounts/:accountId',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-organization-client-accounts-detail',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_CLIENT_ACCOUNTS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_CLIENT_ACCOUNTS_TITLE,
        operationId: OPERATION_IDS.ORGANIZATION_CLIENT_ACCOUNTS_READ,
    },
    {
        id: 'route-organization-client-accounts-edit',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION,
        accessibilityLabelKey: I18N_KEYS.PAGE_CLIENT_ACCOUNTS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.ORGANIZATION_CLIENT_ACCOUNTS_EDIT,
        path: '/admin/client-accounts/:accountId/edit',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-organization-client-accounts-edit',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_CLIENT_ACCOUNTS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_CLIENT_ACCOUNTS_TITLE,
        operationId: OPERATION_IDS.ORGANIZATION_CLIENT_ACCOUNTS_UPDATE,
    },
    {
        id: 'route-organization-portfolios-list',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION,
        accessibilityLabelKey: I18N_KEYS.PAGE_PORTFOLIOS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.ORGANIZATION_PORTFOLIOS_LIST,
        path: '/admin/portfolios',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-organization-portfolios-list',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_PORTFOLIOS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_PORTFOLIOS_TITLE,
        operationId: OPERATION_IDS.ORGANIZATION_PORTFOLIOS_LIST,
    },
    {
        id: 'route-organization-portfolios-create',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION,
        accessibilityLabelKey: I18N_KEYS.PAGE_PORTFOLIOS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.ORGANIZATION_PORTFOLIOS_CREATE,
        path: '/admin/portfolios/new',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-organization-portfolios-create',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_PORTFOLIOS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_PORTFOLIOS_TITLE,
        operationId: OPERATION_IDS.ORGANIZATION_PORTFOLIOS_CREATE,
    },
    {
        id: 'route-organization-portfolios-detail',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION,
        accessibilityLabelKey: I18N_KEYS.PAGE_PORTFOLIOS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.ORGANIZATION_PORTFOLIOS_DETAIL,
        path: '/admin/portfolios/:portfolioId',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-organization-portfolios-detail',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_PORTFOLIOS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_PORTFOLIOS_TITLE,
        operationId: OPERATION_IDS.ORGANIZATION_PORTFOLIOS_READ,
    },
    {
        id: 'route-organization-portfolios-edit',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION,
        accessibilityLabelKey: I18N_KEYS.PAGE_PORTFOLIOS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.ORGANIZATION_PORTFOLIOS_EDIT,
        path: '/admin/portfolios/:portfolioId/edit',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-organization-portfolios-edit',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_PORTFOLIOS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_PORTFOLIOS_TITLE,
        operationId: OPERATION_IDS.ORGANIZATION_PORTFOLIOS_UPDATE,
    },
    {
        id: 'route-admin-opening-cutover-list',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_OPENING_CUTOVER,
        telemetryId: TELEMETRY_IDS.CARD_OPENING_CUTOVER,
        accessibilityLabelKey: I18N_KEYS.PAGE_OPENING_CUTOVER_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        routeId: ROUTE_IDS.ADMIN_OPENING_CUTOVER_LIST,
        path: '/admin/opening-cutover',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-admin-opening-cutover-list',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_OPENING_CUTOVER_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_OPENING_CUTOVER_TITLE,
        operationId: OPERATION_IDS.ACCOUNTING_OPENING_CUTOVER_GET,
    },
    {
        id: 'route-admin-opening-cutover-create',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_OPENING_CUTOVER,
        telemetryId: TELEMETRY_IDS.CARD_OPENING_CUTOVER,
        accessibilityLabelKey: I18N_KEYS.PAGE_OPENING_CUTOVER_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        routeId: ROUTE_IDS.ADMIN_OPENING_CUTOVER_CREATE,
        path: '/admin/opening-cutover/new',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-admin-opening-cutover-create',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_OPENING_CUTOVER_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_OPENING_CUTOVER_TITLE,
        operationId: OPERATION_IDS.ACCOUNTING_OPENING_CUTOVER_PREVIEW,
    },
    {
        id: 'route-admin-opening-cutover-detail',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_OPENING_CUTOVER,
        telemetryId: TELEMETRY_IDS.CARD_OPENING_CUTOVER,
        accessibilityLabelKey: I18N_KEYS.PAGE_OPENING_CUTOVER_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        routeId: ROUTE_IDS.ADMIN_OPENING_CUTOVER_DETAIL,
        path: '/admin/opening-cutover/:batchId',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-admin-opening-cutover-detail',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_OPENING_CUTOVER_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_OPENING_CUTOVER_TITLE,
        operationId: OPERATION_IDS.ACCOUNTING_OPENING_CUTOVER_GET,
    },
    {
        id: 'route-admin-opening-cutover-edit',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_OPENING_CUTOVER,
        telemetryId: TELEMETRY_IDS.CARD_OPENING_CUTOVER,
        accessibilityLabelKey: I18N_KEYS.PAGE_OPENING_CUTOVER_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        routeId: ROUTE_IDS.ADMIN_OPENING_CUTOVER_EDIT,
        path: '/admin/opening-cutover/:batchId/approve',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-admin-opening-cutover-edit',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_OPENING_CUTOVER_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_OPENING_CUTOVER_TITLE,
        operationId: OPERATION_IDS.ACCOUNTING_OPENING_CUTOVER_APPROVE,
    },
    {
        id: 'route-admin-opening-cutover-history',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_OPENING_CUTOVER,
        telemetryId: TELEMETRY_IDS.HUB_ADMIN_SETUP_HISTORY,
        accessibilityLabelKey: I18N_KEYS.PAGE_OPENING_CUTOVER_HISTORY_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        routeId: ROUTE_IDS.ADMIN_OPENING_CUTOVER_HISTORY,
        path: '/admin/opening-cutover/history',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-admin-opening-cutover-history',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_OPENING_CUTOVER_HISTORY_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_OPENING_CUTOVER_HISTORY_TITLE,
        operationId: OPERATION_IDS.ACCOUNTING_OPENING_CUTOVER_GET,
    },
    {
        id: 'route-admin-tax-rules-list',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_TAX_RULES,
        telemetryId: TELEMETRY_IDS.CARD_TAX_RULES,
        accessibilityLabelKey: I18N_KEYS.PAGE_TAX_RULES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        routeId: ROUTE_IDS.ADMIN_TAX_RULES_LIST,
        path: '/admin/tax-rules',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-admin-tax-rules-list',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_TAX_RULES_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_TAX_RULES_TITLE,
        operationId: OPERATION_IDS.TAX_RULE_LIST,
    },
    {
        id: 'route-admin-tax-rules-create',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_TAX_RULES,
        telemetryId: TELEMETRY_IDS.CARD_TAX_RULES,
        accessibilityLabelKey: I18N_KEYS.PAGE_TAX_RULES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        routeId: ROUTE_IDS.ADMIN_TAX_RULES_CREATE,
        path: '/admin/tax-rules/new',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-admin-tax-rules-create',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_TAX_RULES_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_TAX_RULES_TITLE,
        operationId: OPERATION_IDS.TAX_RULE_CREATE_VERSION,
    },
    {
        id: 'route-admin-tax-rules-detail',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_TAX_RULES,
        telemetryId: TELEMETRY_IDS.CARD_TAX_RULES,
        accessibilityLabelKey: I18N_KEYS.PAGE_TAX_RULES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        routeId: ROUTE_IDS.ADMIN_TAX_RULES_DETAIL,
        path: '/admin/tax-rules/:ruleId',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-admin-tax-rules-detail',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_TAX_RULES_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_TAX_RULES_TITLE,
        operationId: OPERATION_IDS.TAX_RULE_READ,
    },
    {
        id: 'route-admin-tax-rules-edit',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_TAX_RULES,
        telemetryId: TELEMETRY_IDS.CARD_TAX_RULES,
        accessibilityLabelKey: I18N_KEYS.PAGE_TAX_RULES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        routeId: ROUTE_IDS.ADMIN_TAX_RULES_EDIT,
        path: '/admin/tax-rules/:ruleId/edit',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-admin-tax-rules-edit',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_TAX_RULES_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_TAX_RULES_TITLE,
        operationId: OPERATION_IDS.TAX_RULE_SUBMIT,
    },
    {
        id: 'route-admin-fee-rules-list',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_FEE_RULES,
        telemetryId: TELEMETRY_IDS.CARD_FEE_RULES,
        accessibilityLabelKey: I18N_KEYS.PAGE_FEE_RULES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        routeId: ROUTE_IDS.ADMIN_FEE_RULES_LIST,
        path: '/admin/fee-rules',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-admin-fee-rules-list',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_FEE_RULES_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_FEE_RULES_TITLE,
        operationId: OPERATION_IDS.FEE_RULE_LIST,
    },
    {
        id: 'route-admin-fee-rules-create',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_FEE_RULES,
        telemetryId: TELEMETRY_IDS.CARD_FEE_RULES,
        accessibilityLabelKey: I18N_KEYS.PAGE_FEE_RULES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        routeId: ROUTE_IDS.ADMIN_FEE_RULES_CREATE,
        path: '/admin/fee-rules/new',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-admin-fee-rules-create',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_FEE_RULES_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_FEE_RULES_TITLE,
        operationId: OPERATION_IDS.FEE_RULE_CREATE_VERSION,
    },
    {
        id: 'route-admin-fee-rules-detail',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_FEE_RULES,
        telemetryId: TELEMETRY_IDS.CARD_FEE_RULES,
        accessibilityLabelKey: I18N_KEYS.PAGE_FEE_RULES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        routeId: ROUTE_IDS.ADMIN_FEE_RULES_DETAIL,
        path: '/admin/fee-rules/:ruleId',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-admin-fee-rules-detail',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_FEE_RULES_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_FEE_RULES_TITLE,
        operationId: OPERATION_IDS.FEE_RULE_READ,
    },
    {
        id: 'route-admin-fee-rules-edit',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_FEE_RULES,
        telemetryId: TELEMETRY_IDS.CARD_FEE_RULES,
        accessibilityLabelKey: I18N_KEYS.PAGE_FEE_RULES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        routeId: ROUTE_IDS.ADMIN_FEE_RULES_EDIT,
        path: '/admin/fee-rules/:ruleId/edit',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-admin-fee-rules-edit',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_FEE_RULES_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_FEE_RULES_TITLE,
        operationId: OPERATION_IDS.FEE_RULE_SUBMIT,
    },
    {
        id: 'route-admin-fiscal-years-list',
        schemaVersion: 1,
        module: MODULE_KEYS.ACCOUNTING,
        feature: FEATURE_KEYS.ADMIN_FISCAL_CALENDAR,
        telemetryId: TELEMETRY_IDS.CARD_FISCAL_CALENDAR,
        accessibilityLabelKey: I18N_KEYS.PAGE_FISCAL_YEARS_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        routeId: ROUTE_IDS.ADMIN_FISCAL_YEARS_LIST,
        path: '/admin/fiscal-years',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-admin-fiscal-years-list',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_FISCAL_YEARS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_FISCAL_YEARS_TITLE,
        operationId: OPERATION_IDS.ACCOUNTING_FISCAL_YEAR_LIST,
    },
    {
        id: 'route-admin-fiscal-year-create',
        schemaVersion: 1,
        module: MODULE_KEYS.ACCOUNTING,
        feature: FEATURE_KEYS.ADMIN_FISCAL_CALENDAR,
        telemetryId: TELEMETRY_IDS.CARD_FISCAL_CALENDAR,
        accessibilityLabelKey: I18N_KEYS.PAGE_FISCAL_YEAR_CREATE_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        routeId: ROUTE_IDS.ADMIN_FISCAL_YEAR_CREATE,
        path: '/admin/fiscal-years/new',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-admin-fiscal-year-create',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_FISCAL_YEARS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_FISCAL_YEAR_CREATE_TITLE,
        operationId: OPERATION_IDS.ACCOUNTING_FISCAL_YEAR_CREATE,
    },
    {
        id: 'route-admin-fiscal-year-detail',
        schemaVersion: 1,
        module: MODULE_KEYS.ACCOUNTING,
        feature: FEATURE_KEYS.ADMIN_FISCAL_CALENDAR,
        telemetryId: TELEMETRY_IDS.CARD_FISCAL_CALENDAR,
        accessibilityLabelKey: I18N_KEYS.PAGE_FISCAL_YEAR_DETAIL_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        routeId: ROUTE_IDS.ADMIN_FISCAL_YEAR_DETAIL,
        path: '/admin/fiscal-years/:id',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-admin-fiscal-year-detail',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_FISCAL_YEARS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_FISCAL_YEAR_DETAIL_TITLE,
        operationId: OPERATION_IDS.ACCOUNTING_FISCAL_YEAR_GET,
    },
    {
        id: 'route-admin-fiscal-year-periods',
        schemaVersion: 1,
        module: MODULE_KEYS.ACCOUNTING,
        feature: FEATURE_KEYS.ADMIN_FISCAL_CALENDAR,
        telemetryId: TELEMETRY_IDS.CARD_FISCAL_CALENDAR,
        accessibilityLabelKey: I18N_KEYS.PAGE_FISCAL_YEAR_PERIODS_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        routeId: ROUTE_IDS.ADMIN_FISCAL_YEAR_PERIODS,
        path: '/admin/fiscal-years/:id/periods',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-admin-fiscal-year-periods',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_FISCAL_YEARS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_FISCAL_YEAR_PERIODS_TITLE,
        operationId: OPERATION_IDS.FISCAL_PERIODS_LIST,
    },
    {
        id: 'route-organization-audit-list',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_AUDIT,
        telemetryId: TELEMETRY_IDS.ADMIN_AUDIT,
        accessibilityLabelKey: I18N_KEYS.PAGE_ORGANIZATION_AUDIT_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.ORGANIZATION_AUDIT_LIST,
        path: '/admin/organization/audit',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-organization-audit-list',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_ORGANIZATION_AUDIT_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_ORGANIZATION_AUDIT_TITLE,
        operationId: OPERATION_IDS.ORGANIZATION_AUDIT_LIST,
    },
    {
        id: 'route-organization-audit-detail',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_AUDIT,
        telemetryId: TELEMETRY_IDS.ADMIN_AUDIT,
        accessibilityLabelKey: I18N_KEYS.PAGE_ORGANIZATION_AUDIT_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.ORGANIZATION_AUDIT_DETAIL,
        path: '/admin/organization/audit/:auditId',
        allowedPersonas: ['ORG_ADMIN', 'SUPPORT_DELEGATE'],
        pageId: 'page-organization-audit-detail',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_ORGANIZATION_AUDIT_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_ORGANIZATION_AUDIT_TITLE,
        operationId: OPERATION_IDS.ORGANIZATION_AUDIT_READ,
    },
    // Data hub
    {
        id: 'route-data-hub',
        schemaVersion: 1,
        module: MODULE_KEYS.DATA,
        feature: FEATURE_KEYS.DATA_HUB,
        telemetryId: TELEMETRY_IDS.DATA_HUB,
        accessibilityLabelKey: I18N_KEYS.HUB_DATA_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        routeId: ROUTE_IDS.DATA_HUB,
        path: '/data',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'ORG_CLIENT', 'SUPPORT_DELEGATE'],
        pageId: 'page-data-hub',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA],
        navigationPlacement: 'primary',
        iconId: ICON_IDS.DATA,
        titleKey: I18N_KEYS.HUB_DATA_TITLE,
        operationId: OPERATION_IDS.DATA_HUB,
    },
    {
        id: 'route-data-instruments',
        schemaVersion: 1,
        module: MODULE_KEYS.INSTRUMENTS,
        feature: FEATURE_KEYS.DATA_INSTRUMENTS,
        telemetryId: TELEMETRY_IDS.DATA_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_INSTRUMENTS_TITLE,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.DATA_INSTRUMENTS,
        path: '/data/instruments',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'SUPPORT_DELEGATE'],
        pageId: 'page-data-instruments',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.PAGE_INSTRUMENTS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_INSTRUMENTS_TITLE,
        operationId: OPERATION_IDS.INSTRUMENT_LIST,
    },
    {
        id: 'route-data-instrument-detail',
        schemaVersion: 1,
        module: MODULE_KEYS.INSTRUMENTS,
        feature: FEATURE_KEYS.DATA_INSTRUMENTS,
        telemetryId: TELEMETRY_IDS.DATA_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_INSTRUMENTS_TITLE,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.DATA_INSTRUMENT_DETAIL,
        path: '/data/instruments/:symbol',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'SUPPORT_DELEGATE'],
        pageId: 'page-data-instrument-detail',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.PAGE_INSTRUMENTS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_INSTRUMENTS_TITLE,
        operationId: OPERATION_IDS.INSTRUMENT_GET,
    },
    {
        id: 'route-data-instrument-create',
        schemaVersion: 1,
        module: MODULE_KEYS.INSTRUMENTS,
        feature: FEATURE_KEYS.DATA_INSTRUMENTS,
        telemetryId: TELEMETRY_IDS.DATA_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_INSTRUMENTS_TITLE,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.DATA_INSTRUMENT_CREATE,
        path: '/data/instruments/new',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER'],
        pageId: 'page-data-instrument-create',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.PAGE_INSTRUMENTS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_INSTRUMENTS_TITLE,
        operationId: OPERATION_IDS.INSTRUMENT_CREATE,
    },
    {
        id: 'route-data-instrument-edit',
        schemaVersion: 1,
        module: MODULE_KEYS.INSTRUMENTS,
        feature: FEATURE_KEYS.DATA_INSTRUMENTS,
        telemetryId: TELEMETRY_IDS.DATA_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_INSTRUMENTS_TITLE,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.DATA_INSTRUMENT_EDIT,
        path: '/data/instruments/:symbol/edit',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER'],
        pageId: 'page-data-instrument-edit',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.PAGE_INSTRUMENTS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_INSTRUMENTS_TITLE,
        operationId: OPERATION_IDS.INSTRUMENT_UPDATE,
    },
    {
        id: 'route-data-imports',
        schemaVersion: 1,
        module: MODULE_KEYS.IMPORT,
        feature: FEATURE_KEYS.DATA_IMPORTS,
        telemetryId: TELEMETRY_IDS.DATA_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_IMPORTS_TITLE,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.DATA_IMPORTS,
        path: '/data/imports',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'SUPPORT_DELEGATE'],
        pageId: 'page-data-imports',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.PAGE_IMPORTS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_IMPORTS_TITLE,
        operationId: OPERATION_IDS.IMPORT_LIST,
    },
    {
        id: 'route-data-import-upload',
        schemaVersion: 1,
        module: MODULE_KEYS.IMPORT,
        feature: FEATURE_KEYS.DATA_IMPORTS,
        telemetryId: TELEMETRY_IDS.DATA_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_IMPORTS_TITLE,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.DATA_IMPORT_UPLOAD,
        path: '/data/imports/upload',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER'],
        pageId: 'page-data-import-upload',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.PAGE_IMPORTS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_IMPORTS_TITLE,
        operationId: OPERATION_IDS.IMPORT_UPLOAD,
    },
    {
        id: 'route-data-import-detail',
        schemaVersion: 1,
        module: MODULE_KEYS.IMPORT,
        feature: FEATURE_KEYS.DATA_IMPORT_DETAIL,
        telemetryId: TELEMETRY_IDS.DATA_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_IMPORTS_TITLE,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.DATA_IMPORT_DETAIL,
        path: '/data/imports/:id',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'SUPPORT_DELEGATE'],
        pageId: 'page-data-import-detail',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.PAGE_IMPORTS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_IMPORTS_TITLE,
        operationId: OPERATION_IDS.IMPORT_GET,
    },
    {
        id: 'route-data-reconciliation',
        schemaVersion: 1,
        module: MODULE_KEYS.RECONCILIATION,
        feature: FEATURE_KEYS.DATA_RECONCILIATION,
        telemetryId: TELEMETRY_IDS.DATA_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_RECONCILIATION_TITLE,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.DATA_RECONCILIATION,
        path: '/data/reconciliation',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'SUPPORT_DELEGATE'],
        pageId: 'page-data-reconciliation',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.PAGE_RECONCILIATION_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_RECONCILIATION_TITLE,
        operationId: OPERATION_IDS.RECONCILIATION_LIST,
    },
    {
        id: 'route-data-reconciliation-detail',
        schemaVersion: 1,
        module: MODULE_KEYS.RECONCILIATION,
        feature: FEATURE_KEYS.DATA_RECONCILIATION_DETAIL,
        telemetryId: TELEMETRY_IDS.DATA_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_RECONCILIATION_TITLE,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.DATA_RECONCILIATION_DETAIL,
        path: '/data/reconciliation/runs/:runId',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'SUPPORT_DELEGATE'],
        pageId: 'page-data-reconciliation-detail',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.PAGE_RECONCILIATION_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_RECONCILIATION_TITLE,
        operationId: OPERATION_IDS.RECONCILIATION_GET,
    },
    {
        id: 'route-data-reconciliation-create',
        schemaVersion: 1,
        module: MODULE_KEYS.RECONCILIATION,
        feature: FEATURE_KEYS.DATA_RECONCILIATION,
        telemetryId: TELEMETRY_IDS.DATA_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_RECONCILIATION_TITLE,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.DATA_RECONCILIATION_CREATE,
        path: '/data/reconciliation/new',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER'],
        pageId: 'page-data-reconciliation-create',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.PAGE_RECONCILIATION_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_RECONCILIATION_TITLE,
        operationId: OPERATION_IDS.RECONCILIATION_CREATE,
    },
    {
        id: 'route-data-corporate-actions',
        schemaVersion: 1,
        module: MODULE_KEYS.CORPORATE_ACTIONS,
        feature: FEATURE_KEYS.DATA_CORPORATE_ACTIONS,
        telemetryId: TELEMETRY_IDS.DATA_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_CORPORATE_ACTIONS_TITLE,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.DATA_CORPORATE_ACTIONS,
        path: '/data/corporate-actions',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'SUPPORT_DELEGATE'],
        pageId: 'page-data-corporate-actions',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.PAGE_CORPORATE_ACTIONS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_CORPORATE_ACTIONS_TITLE,
        operationId: OPERATION_IDS.CORPORATE_ACTION_LIST,
    },
    {
        id: 'route-data-corporate-action-detail',
        schemaVersion: 1,
        module: MODULE_KEYS.CORPORATE_ACTIONS,
        feature: FEATURE_KEYS.DATA_CORPORATE_ACTION_DETAIL,
        telemetryId: TELEMETRY_IDS.DATA_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_CORPORATE_ACTIONS_TITLE,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.DATA_CORPORATE_ACTION_DETAIL,
        path: '/data/corporate-actions/:id',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'SUPPORT_DELEGATE'],
        pageId: 'page-data-corporate-action-detail',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.PAGE_CORPORATE_ACTIONS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_CORPORATE_ACTIONS_TITLE,
        operationId: OPERATION_IDS.CORPORATE_ACTION_GET,
    },
    {
        id: 'route-data-corporate-action-create',
        schemaVersion: 1,
        module: MODULE_KEYS.CORPORATE_ACTIONS,
        feature: FEATURE_KEYS.DATA_CORPORATE_ACTIONS,
        telemetryId: TELEMETRY_IDS.DATA_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_CORPORATE_ACTIONS_TITLE,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.DATA_CORPORATE_ACTION_CREATE,
        path: '/data/corporate-actions/new',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER'],
        pageId: 'page-data-corporate-action-create',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.PAGE_CORPORATE_ACTIONS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_CORPORATE_ACTIONS_TITLE,
        operationId: OPERATION_IDS.CORPORATE_ACTION_CREATE,
    },
    {
        id: 'route-data-corporate-action-edit',
        schemaVersion: 1,
        module: MODULE_KEYS.CORPORATE_ACTIONS,
        feature: FEATURE_KEYS.DATA_CORPORATE_ACTIONS,
        telemetryId: TELEMETRY_IDS.DATA_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_CORPORATE_ACTIONS_TITLE,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.DATA_CORPORATE_ACTION_EDIT,
        path: '/data/corporate-actions/:id/edit',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER'],
        pageId: 'page-data-corporate-action-edit',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.PAGE_CORPORATE_ACTIONS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_CORPORATE_ACTIONS_TITLE,
        operationId: OPERATION_IDS.CORPORATE_ACTION_UPDATE,
    },
    {
        id: 'route-data-holdings',
        schemaVersion: 1,
        module: MODULE_KEYS.HOLDINGS,
        feature: FEATURE_KEYS.DATA_HOLDINGS,
        telemetryId: TELEMETRY_IDS.DATA_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_HOLDINGS_TITLE,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.DATA_HOLDINGS,
        path: '/data/holdings',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'SUPPORT_DELEGATE'],
        pageId: 'page-data-holdings',
        layoutId: 'app',
        authentication: 'authenticated',
        // Legacy holdings route redirects to the canonical positions surface.
        availability: 'REDIRECT',
        redirectRouteId: ROUTE_IDS.DATA_POSITIONS,
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.PAGE_HOLDINGS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_HOLDINGS_TITLE,
        operationId: OPERATION_IDS.HOLDINGS_VIEW,
    },
    {
        id: 'route-data-holdings-detail',
        schemaVersion: 1,
        module: MODULE_KEYS.HOLDINGS,
        feature: FEATURE_KEYS.DATA_HOLDINGS_DETAIL,
        telemetryId: TELEMETRY_IDS.DATA_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_HOLDINGS_TITLE,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.DATA_HOLDINGS_DETAIL,
        path: '/data/holdings/:holdingId',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'SUPPORT_DELEGATE'],
        pageId: 'page-data-holdings-detail',
        layoutId: 'app',
        authentication: 'authenticated',
        // Legacy holdings detail redirects to the canonical positions detail surface.
        availability: 'REDIRECT',
        redirectRouteId: ROUTE_IDS.DATA_POSITIONS_DETAIL,
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.PAGE_HOLDINGS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_HOLDINGS_TITLE,
        operationId: OPERATION_IDS.HOLDINGS_VIEW,
    },
    {
        id: 'route-data-fact-batches',
        schemaVersion: 1,
        module: MODULE_KEYS.FACTS,
        feature: FEATURE_KEYS.DATA_FACT_BATCHES,
        telemetryId: TELEMETRY_IDS.DATA_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_FACT_BATCHES_TITLE,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.DATA_FACT_BATCHES,
        path: '/data/fact-batches',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'SUPPORT_DELEGATE'],
        pageId: 'page-data-fact-batches',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.PAGE_FACT_BATCHES_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_FACT_BATCHES_TITLE,
        operationId: OPERATION_IDS.FACT_BATCHES_LIST,
    },
    {
        id: 'route-data-fact-batch-detail',
        schemaVersion: 1,
        module: MODULE_KEYS.FACTS,
        feature: FEATURE_KEYS.DATA_FACT_BATCH_DETAIL,
        telemetryId: TELEMETRY_IDS.DATA_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_FACT_BATCHES_TITLE,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.DATA_FACT_BATCH_DETAIL,
        path: '/data/fact-batches/:id',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'SUPPORT_DELEGATE'],
        pageId: 'page-data-fact-batch-detail',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.PAGE_FACT_BATCHES_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_FACT_BATCHES_TITLE,
        operationId: OPERATION_IDS.FACT_BATCH_GET,
    },
    {
        id: 'route-data-fact-batch-create',
        schemaVersion: 1,
        module: MODULE_KEYS.FACTS,
        feature: FEATURE_KEYS.DATA_FACT_BATCHES,
        telemetryId: TELEMETRY_IDS.DATA_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_FACT_BATCHES_TITLE,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.DATA_FACT_BATCH_CREATE,
        path: '/data/fact-batches/new',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER'],
        pageId: 'page-data-fact-batch-create',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.PAGE_FACT_BATCHES_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_FACT_BATCHES_TITLE,
        operationId: OPERATION_IDS.FACT_CREATE,
    },
    // ── P2 hardening: new Data Hub route entries (PLANNED) ──────────────────
    // Market Data
    {
        id: 'route-data-market-prices',
        schemaVersion: 1,
        module: MODULE_KEYS.MARKET_DATA,
        feature: FEATURE_KEYS.DATA_MARKET_PRICES,
        telemetryId: TELEMETRY_IDS.HUB_DATA_MARKET,
        accessibilityLabelKey: I18N_KEYS.CARD_MARKET_PRICES_TITLE,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        routeId: ROUTE_IDS.DATA_MARKET_PRICES,
        path: '/data/market-prices',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'SUPPORT_DELEGATE'],
        pageId: 'page-data-market-prices',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.CARD_MARKET_PRICES_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.CARD_MARKET_PRICES_TITLE,
        operationId: OPERATION_IDS.MARKET_PRICES_LIST,
    },
    {
        id: 'route-data-market-price-detail',
        schemaVersion: 1,
        module: MODULE_KEYS.MARKET_DATA,
        feature: FEATURE_KEYS.DATA_MARKET_PRICES,
        telemetryId: TELEMETRY_IDS.HUB_DATA_MARKET,
        accessibilityLabelKey: I18N_KEYS.CARD_MARKET_PRICES_TITLE,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        routeId: ROUTE_IDS.DATA_MARKET_PRICE_DETAIL,
        path: '/data/market-prices/:id',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'SUPPORT_DELEGATE'],
        pageId: 'page-data-market-price-detail',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.CARD_MARKET_PRICES_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.CARD_MARKET_PRICES_TITLE,
        operationId: OPERATION_IDS.MARKET_PRICES_GET,
    },
    {
        id: 'route-data-fx-rates',
        schemaVersion: 1,
        module: MODULE_KEYS.MARKET_DATA,
        feature: FEATURE_KEYS.DATA_FX_RATES,
        telemetryId: TELEMETRY_IDS.HUB_DATA_MARKET,
        accessibilityLabelKey: I18N_KEYS.CARD_FX_RATES_TITLE,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        routeId: ROUTE_IDS.DATA_FX_RATES,
        path: '/data/fx-rates',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'SUPPORT_DELEGATE'],
        pageId: 'page-data-fx-rates',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.CARD_FX_RATES_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.CARD_FX_RATES_TITLE,
        operationId: OPERATION_IDS.FX_RATES_LIST,
    },
    {
        id: 'route-data-benchmarks',
        schemaVersion: 1,
        module: MODULE_KEYS.MARKET_DATA,
        feature: FEATURE_KEYS.DATA_BENCHMARKS,
        telemetryId: TELEMETRY_IDS.HUB_DATA_MARKET,
        accessibilityLabelKey: I18N_KEYS.CARD_BENCHMARKS_TITLE,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        routeId: ROUTE_IDS.DATA_BENCHMARKS,
        path: '/data/benchmarks',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'SUPPORT_DELEGATE'],
        pageId: 'page-data-benchmarks',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.CARD_BENCHMARKS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.CARD_BENCHMARKS_TITLE,
        operationId: OPERATION_IDS.BENCHMARKS_LIST,
    },
    {
        id: 'route-data-market-data-providers',
        schemaVersion: 1,
        module: MODULE_KEYS.MARKET_DATA,
        feature: FEATURE_KEYS.DATA_MARKET_DATA_PROVIDERS,
        telemetryId: TELEMETRY_IDS.HUB_DATA_MARKET,
        accessibilityLabelKey: I18N_KEYS.CARD_MARKET_DATA_PROVIDERS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        routeId: ROUTE_IDS.DATA_MARKET_DATA_PROVIDERS,
        path: '/data/market-data-providers',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'SUPPORT_DELEGATE'],
        pageId: 'page-data-market-data-providers',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.CARD_MARKET_DATA_PROVIDERS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.CARD_MARKET_DATA_PROVIDERS_TITLE,
        operationId: OPERATION_IDS.MARKET_DATA_PROVIDERS_LIST,
    },
    {
        id: 'route-data-market-data-exceptions',
        schemaVersion: 1,
        module: MODULE_KEYS.MARKET_DATA,
        feature: FEATURE_KEYS.DATA_MARKET_DATA_EXCEPTIONS,
        telemetryId: TELEMETRY_IDS.HUB_DATA_MARKET,
        accessibilityLabelKey: I18N_KEYS.CARD_MARKET_DATA_EXCEPTIONS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        routeId: ROUTE_IDS.DATA_MARKET_DATA_EXCEPTIONS,
        path: '/data/market-data-exceptions',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'SUPPORT_DELEGATE'],
        pageId: 'page-data-market-data-exceptions',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.CARD_MARKET_DATA_EXCEPTIONS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.CARD_MARKET_DATA_EXCEPTIONS_TITLE,
        operationId: OPERATION_IDS.MARKET_DATA_EXCEPTIONS_LIST,
    },
    // Portfolio Management
    {
        id: 'route-data-clients',
        schemaVersion: 1,
        module: MODULE_KEYS.PORTFOLIO_MGMT,
        feature: FEATURE_KEYS.DATA_CLIENTS,
        telemetryId: TELEMETRY_IDS.HUB_DATA_PORTFOLIO,
        accessibilityLabelKey: I18N_KEYS.CARD_CLIENTS_DATA_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.DATA_CLIENTS,
        path: '/data/clients',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'SUPPORT_DELEGATE'],
        pageId: 'page-data-clients',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.CARD_CLIENTS_DATA_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.CARD_CLIENTS_DATA_TITLE,
        operationId: OPERATION_IDS.DATA_CLIENTS_LIST,
    },
    {
        id: 'route-data-client-detail',
        schemaVersion: 1,
        module: MODULE_KEYS.PORTFOLIO_MGMT,
        feature: FEATURE_KEYS.DATA_CLIENTS,
        telemetryId: TELEMETRY_IDS.HUB_DATA_PORTFOLIO,
        accessibilityLabelKey: I18N_KEYS.CARD_CLIENTS_DATA_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.DATA_CLIENT_DETAIL,
        path: '/data/clients/:id',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'SUPPORT_DELEGATE'],
        pageId: 'page-data-client-detail',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.CARD_CLIENTS_DATA_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.CARD_CLIENTS_DATA_TITLE,
        operationId: OPERATION_IDS.DATA_CLIENTS_GET,
    },
    {
        id: 'route-data-portfolios',
        schemaVersion: 1,
        module: MODULE_KEYS.PORTFOLIO_MGMT,
        feature: FEATURE_KEYS.DATA_PORTFOLIOS,
        telemetryId: TELEMETRY_IDS.HUB_DATA_PORTFOLIO,
        accessibilityLabelKey: I18N_KEYS.CARD_PORTFOLIOS_DATA_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.DATA_PORTFOLIOS,
        path: '/data/portfolios',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'SUPPORT_DELEGATE'],
        pageId: 'page-data-portfolios',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.CARD_PORTFOLIOS_DATA_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.CARD_PORTFOLIOS_DATA_TITLE,
        operationId: OPERATION_IDS.DATA_PORTFOLIOS_LIST,
    },
    {
        id: 'route-data-portfolio-detail',
        schemaVersion: 1,
        module: MODULE_KEYS.PORTFOLIO_MGMT,
        feature: FEATURE_KEYS.DATA_PORTFOLIOS,
        telemetryId: TELEMETRY_IDS.HUB_DATA_PORTFOLIO,
        accessibilityLabelKey: I18N_KEYS.CARD_PORTFOLIOS_DATA_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.DATA_PORTFOLIO_DETAIL,
        path: '/data/portfolios/:id',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'SUPPORT_DELEGATE'],
        pageId: 'page-data-portfolio-detail',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.CARD_PORTFOLIOS_DATA_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.CARD_PORTFOLIOS_DATA_TITLE,
        operationId: OPERATION_IDS.DATA_PORTFOLIOS_GET,
    },
    {
        id: 'route-data-mandates',
        schemaVersion: 1,
        module: MODULE_KEYS.PORTFOLIO_MGMT,
        feature: FEATURE_KEYS.DATA_MANDATES,
        telemetryId: TELEMETRY_IDS.HUB_DATA_PORTFOLIO,
        accessibilityLabelKey: I18N_KEYS.CARD_MANDATES_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.DATA_MANDATES,
        path: '/data/mandates',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'SUPPORT_DELEGATE'],
        pageId: 'page-data-mandates',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.CARD_MANDATES_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.CARD_MANDATES_TITLE,
        operationId: OPERATION_IDS.DATA_MANDATES_LIST,
    },
    {
        id: 'route-data-positions',
        schemaVersion: 1,
        module: MODULE_KEYS.PORTFOLIO_MGMT,
        feature: FEATURE_KEYS.DATA_POSITIONS,
        telemetryId: TELEMETRY_IDS.HUB_DATA_PORTFOLIO,
        accessibilityLabelKey: I18N_KEYS.CARD_POSITIONS_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.DATA_POSITIONS,
        path: '/data/positions',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'SUPPORT_DELEGATE'],
        pageId: 'page-data-positions',
        layoutId: 'app',
        authentication: 'authenticated',
        // Positions is the canonical financial-aggregate surface for portfolio holdings.
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.CARD_POSITIONS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.CARD_POSITIONS_TITLE,
        operationId: OPERATION_IDS.DATA_POSITIONS_LIST,
    },
    {
        id: 'route-data-positions-detail',
        schemaVersion: 1,
        module: MODULE_KEYS.PORTFOLIO_MGMT,
        feature: FEATURE_KEYS.DATA_POSITIONS_DETAIL,
        telemetryId: TELEMETRY_IDS.HUB_DATA_PORTFOLIO,
        accessibilityLabelKey: I18N_KEYS.CARD_POSITIONS_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.DATA_POSITIONS_DETAIL,
        path: '/data/positions/:positionId',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'SUPPORT_DELEGATE'],
        pageId: 'page-data-positions-detail',
        layoutId: 'app',
        authentication: 'authenticated',
        // Positions detail is the canonical financial-aggregate detail surface.
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.CARD_POSITIONS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.CARD_POSITIONS_TITLE,
        operationId: OPERATION_IDS.DATA_POSITIONS_GET,
    },
    {
        id: 'route-data-cash',
        schemaVersion: 1,
        module: MODULE_KEYS.PORTFOLIO_MGMT,
        feature: FEATURE_KEYS.DATA_CASH,
        telemetryId: TELEMETRY_IDS.HUB_DATA_PORTFOLIO,
        accessibilityLabelKey: I18N_KEYS.CARD_CASH_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.DATA_CASH,
        path: '/data/cash',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'SUPPORT_DELEGATE'],
        pageId: 'page-data-cash',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.CARD_CASH_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.CARD_CASH_TITLE,
        operationId: OPERATION_IDS.DATA_CASH_LIST,
    },
    {
        id: 'route-data-cash-detail',
        schemaVersion: 1,
        module: MODULE_KEYS.PORTFOLIO_MGMT,
        feature: FEATURE_KEYS.DATA_CASH_DETAIL,
        telemetryId: TELEMETRY_IDS.HUB_DATA_PORTFOLIO,
        accessibilityLabelKey: I18N_KEYS.CARD_CASH_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.DATA_CASH_DETAIL,
        path: '/data/cash/:cashId',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'SUPPORT_DELEGATE'],
        pageId: 'page-data-cash-detail',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.CARD_CASH_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.CARD_CASH_TITLE,
        operationId: OPERATION_IDS.DATA_CASH_GET,
    },
    {
        id: 'route-data-performance',
        schemaVersion: 1,
        module: MODULE_KEYS.PORTFOLIO_MGMT,
        feature: FEATURE_KEYS.DATA_PERFORMANCE,
        telemetryId: TELEMETRY_IDS.HUB_DATA_PORTFOLIO,
        accessibilityLabelKey: I18N_KEYS.CARD_PERFORMANCE_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.DATA_PERFORMANCE,
        path: '/data/performance',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'SUPPORT_DELEGATE'],
        pageId: 'page-data-performance',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.CARD_PERFORMANCE_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.CARD_PERFORMANCE_TITLE,
        operationId: OPERATION_IDS.DATA_PERFORMANCE_LIST,
    },
    {
        id: 'route-data-position-transfers',
        schemaVersion: 1,
        module: MODULE_KEYS.PORTFOLIO_MGMT,
        feature: FEATURE_KEYS.DATA_POSITION_TRANSFERS,
        telemetryId: TELEMETRY_IDS.CARD_POSITION_TRANSFERS,
        accessibilityLabelKey: I18N_KEYS.CARD_POSITION_TRANSFERS_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.DATA_POSITION_TRANSFERS,
        path: '/data/position-transfers',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER'],
        pageId: 'page-data-position-transfers',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.CARD_POSITION_TRANSFERS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.CARD_POSITION_TRANSFERS_TITLE,
        operationId: OPERATION_IDS.POSITION_TRANSFERS_LIST,
    },
    {
        id: 'route-data-management-fees',
        schemaVersion: 1,
        module: MODULE_KEYS.ACCOUNTING,
        feature: FEATURE_KEYS.DATA_MANAGEMENT_FEES,
        telemetryId: TELEMETRY_IDS.CARD_MANAGEMENT_FEES,
        accessibilityLabelKey: I18N_KEYS.CARD_MANAGEMENT_FEES_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'ALWAYS',
        owner: 'accounting',
        routeId: ROUTE_IDS.DATA_MANAGEMENT_FEES,
        path: '/accounting/management-fees',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER'],
        pageId: 'page-data-management-fees',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.CARD_MANAGEMENT_FEES_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.CARD_MANAGEMENT_FEES_TITLE,
        operationId: 'accounting.management-fee.list',
    },
    {
        id: 'route-data-performance-fees',
        schemaVersion: 1,
        module: MODULE_KEYS.ACCOUNTING,
        feature: FEATURE_KEYS.DATA_PERFORMANCE_FEES,
        telemetryId: TELEMETRY_IDS.CARD_PERFORMANCE_FEES,
        accessibilityLabelKey: I18N_KEYS.CARD_PERFORMANCE_FEES_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'ALWAYS',
        owner: 'accounting',
        routeId: ROUTE_IDS.DATA_PERFORMANCE_FEES,
        path: '/accounting/performance-fees',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER'],
        pageId: 'page-data-performance-fees',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.CARD_PERFORMANCE_FEES_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.CARD_PERFORMANCE_FEES_TITLE,
        operationId: 'accounting.performance-fee.list',
    },
    // Settlement and Custody
    {
        id: 'route-data-settlement-instructions',
        schemaVersion: 1,
        module: MODULE_KEYS.SETTLEMENT,
        feature: FEATURE_KEYS.DATA_SETTLEMENT_INSTRUCTIONS,
        telemetryId: TELEMETRY_IDS.HUB_DATA_SETTLEMENT,
        accessibilityLabelKey: I18N_KEYS.CARD_SETTLEMENT_INSTRUCTIONS_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.DATA_SETTLEMENT_INSTRUCTIONS,
        path: '/data/settlement-instructions',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'SUPPORT_DELEGATE'],
        pageId: 'page-data-settlement-instructions',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.CARD_SETTLEMENT_INSTRUCTIONS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.CARD_SETTLEMENT_INSTRUCTIONS_TITLE,
        operationId: OPERATION_IDS.SETTLEMENT_INSTRUCTIONS_LIST,
    },
    {
        id: 'route-data-custody-balances',
        schemaVersion: 1,
        module: MODULE_KEYS.SETTLEMENT,
        feature: FEATURE_KEYS.DATA_CUSTODY_BALANCES,
        telemetryId: TELEMETRY_IDS.HUB_DATA_SETTLEMENT,
        accessibilityLabelKey: I18N_KEYS.CARD_CUSTODY_BALANCES_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.DATA_CUSTODY_BALANCES,
        path: '/data/custody-balances',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'SUPPORT_DELEGATE'],
        pageId: 'page-data-custody-balances',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.CARD_CUSTODY_BALANCES_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.CARD_CUSTODY_BALANCES_TITLE,
        operationId: OPERATION_IDS.CUSTODY_BALANCES_LIST,
    },
    {
        id: 'route-data-settlement-failures',
        schemaVersion: 1,
        module: MODULE_KEYS.SETTLEMENT,
        feature: FEATURE_KEYS.DATA_SETTLEMENT_INSTRUCTIONS,
        telemetryId: TELEMETRY_IDS.HUB_DATA_SETTLEMENT,
        accessibilityLabelKey: I18N_KEYS.CARD_SETTLEMENT_INSTRUCTIONS_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.DATA_SETTLEMENT_FAILURES,
        path: '/data/settlement-failures',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'SUPPORT_DELEGATE'],
        pageId: 'page-data-settlement-failures',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.CARD_SETTLEMENT_INSTRUCTIONS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.CARD_SETTLEMENT_INSTRUCTIONS_TITLE,
        operationId: OPERATION_IDS.SETTLEMENT_FAILURES_LIST,
    },
    // Accounting
    {
        id: 'route-data-journals',
        schemaVersion: 1,
        module: MODULE_KEYS.ACCOUNTING,
        feature: FEATURE_KEYS.DATA_JOURNALS,
        telemetryId: TELEMETRY_IDS.HUB_DATA_ACCOUNTING,
        accessibilityLabelKey: I18N_KEYS.CARD_JOURNALS_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.DATA_JOURNALS,
        path: '/data/journals',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'SUPPORT_DELEGATE'],
        pageId: 'page-data-journals',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.CARD_JOURNALS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.CARD_JOURNALS_TITLE,
        operationId: OPERATION_IDS.JOURNALS_LIST,
    },
    {
        id: 'route-data-journal-detail',
        schemaVersion: 1,
        module: MODULE_KEYS.ACCOUNTING,
        feature: FEATURE_KEYS.DATA_JOURNALS,
        telemetryId: TELEMETRY_IDS.HUB_DATA_ACCOUNTING,
        accessibilityLabelKey: I18N_KEYS.CARD_JOURNALS_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.DATA_JOURNAL_DETAIL,
        path: '/data/journals/:id',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'SUPPORT_DELEGATE'],
        pageId: 'page-data-journal-detail',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.CARD_JOURNALS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.CARD_JOURNALS_TITLE,
        operationId: OPERATION_IDS.JOURNALS_GET,
    },
    {
        id: 'route-data-journal-create',
        schemaVersion: 1,
        module: MODULE_KEYS.ACCOUNTING,
        feature: FEATURE_KEYS.DATA_JOURNALS,
        telemetryId: TELEMETRY_IDS.HUB_DATA_ACCOUNTING,
        accessibilityLabelKey: I18N_KEYS.CARD_JOURNALS_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.DATA_JOURNAL_CREATE,
        path: '/data/journals/new',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER'],
        pageId: 'page-data-journal-create',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.CARD_JOURNALS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.CARD_JOURNALS_TITLE,
        operationId: OPERATION_IDS.JOURNALS_CREATE,
    },
    {
        id: 'route-data-chart-of-accounts',
        schemaVersion: 1,
        module: MODULE_KEYS.ACCOUNTING,
        feature: FEATURE_KEYS.DATA_CHART_OF_ACCOUNTS,
        telemetryId: TELEMETRY_IDS.HUB_DATA_ACCOUNTING,
        accessibilityLabelKey: I18N_KEYS.CARD_CHART_OF_ACCOUNTS_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.DATA_CHART_OF_ACCOUNTS,
        path: '/data/chart-of-accounts',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'SUPPORT_DELEGATE'],
        pageId: 'page-data-chart-of-accounts',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.CARD_CHART_OF_ACCOUNTS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.CARD_CHART_OF_ACCOUNTS_TITLE,
        operationId: OPERATION_IDS.CHART_OF_ACCOUNTS_LIST,
    },
    {
        id: 'route-data-fiscal-periods',
        schemaVersion: 1,
        module: MODULE_KEYS.ACCOUNTING,
        feature: FEATURE_KEYS.DATA_FISCAL_PERIODS,
        telemetryId: TELEMETRY_IDS.HUB_DATA_ACCOUNTING,
        accessibilityLabelKey: I18N_KEYS.CARD_FISCAL_PERIODS_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.DATA_FISCAL_PERIODS,
        path: '/data/fiscal-periods',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'SUPPORT_DELEGATE'],
        pageId: 'page-data-fiscal-periods',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.CARD_FISCAL_PERIODS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.CARD_FISCAL_PERIODS_TITLE,
        operationId: OPERATION_IDS.FISCAL_PERIODS_LIST,
    },
    // Compliance and Risk
    {
        id: 'route-data-compliance-breaches',
        schemaVersion: 1,
        module: MODULE_KEYS.COMPLIANCE,
        feature: FEATURE_KEYS.DATA_COMPLIANCE_BREACHES,
        telemetryId: TELEMETRY_IDS.HUB_DATA_COMPLIANCE,
        accessibilityLabelKey: I18N_KEYS.CARD_COMPLIANCE_BREACHES_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.DATA_COMPLIANCE_BREACHES,
        path: '/data/compliance-breaches',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'SUPPORT_DELEGATE'],
        pageId: 'page-data-compliance-breaches',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.CARD_COMPLIANCE_BREACHES_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.CARD_COMPLIANCE_BREACHES_TITLE,
        operationId: OPERATION_IDS.COMPLIANCE_BREACHES_LIST,
    },
    {
        id: 'route-data-risk-exposures',
        schemaVersion: 1,
        module: MODULE_KEYS.RISK,
        feature: FEATURE_KEYS.DATA_RISK_EXPOSURES,
        telemetryId: TELEMETRY_IDS.HUB_DATA_COMPLIANCE,
        accessibilityLabelKey: I18N_KEYS.CARD_RISK_EXPOSURES_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.DATA_RISK_EXPOSURES,
        path: '/data/risk-exposures',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'SUPPORT_DELEGATE'],
        pageId: 'page-data-risk-exposures',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.CARD_RISK_EXPOSURES_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.CARD_RISK_EXPOSURES_TITLE,
        operationId: OPERATION_IDS.RISK_EXPOSURES_LIST,
    },
    {
        id: 'route-data-compliance-breach-detail',
        schemaVersion: 1,
        module: MODULE_KEYS.COMPLIANCE,
        feature: FEATURE_KEYS.DATA_COMPLIANCE_BREACHES,
        telemetryId: TELEMETRY_IDS.HUB_DATA_COMPLIANCE,
        accessibilityLabelKey: I18N_KEYS.CARD_COMPLIANCE_BREACHES_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.DATA_COMPLIANCE_BREACH_DETAIL,
        path: '/data/compliance-breaches/:id',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'SUPPORT_DELEGATE'],
        pageId: 'page-data-compliance-breach-detail',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.CARD_COMPLIANCE_BREACHES_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.CARD_COMPLIANCE_BREACHES_TITLE,
        operationId: OPERATION_IDS.COMPLIANCE_BREACHES_GET,
    },
    // Tax
    {
        id: 'route-data-tax-records',
        schemaVersion: 1,
        module: MODULE_KEYS.TAX,
        feature: FEATURE_KEYS.DATA_TAX_RECORDS,
        telemetryId: TELEMETRY_IDS.HUB_DATA_TAX,
        accessibilityLabelKey: I18N_KEYS.CARD_TAX_RECORDS_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.DATA_TAX_RECORDS,
        path: '/data/tax-records',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'SUPPORT_DELEGATE'],
        pageId: 'page-data-tax-records',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.CARD_TAX_RECORDS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.CARD_TAX_RECORDS_TITLE,
        operationId: OPERATION_IDS.TAX_RECORDS_LIST,
    },
    {
        id: 'route-data-tax-calculations',
        schemaVersion: 1,
        module: MODULE_KEYS.TAX,
        feature: FEATURE_KEYS.DATA_TAX_RECORDS,
        telemetryId: TELEMETRY_IDS.HUB_DATA_TAX,
        accessibilityLabelKey: I18N_KEYS.CARD_TAX_RECORDS_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.DATA_TAX_CALCULATIONS,
        path: '/data/tax-calculations',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'SUPPORT_DELEGATE'],
        pageId: 'page-data-tax-calculations',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.CARD_TAX_RECORDS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.CARD_TAX_RECORDS_TITLE,
        operationId: OPERATION_IDS.TAX_CALCULATIONS_LIST,
    },
    // Evidence
    {
        id: 'route-data-evidence',
        schemaVersion: 1,
        module: MODULE_KEYS.DOCUMENTS,
        feature: FEATURE_KEYS.DATA_EVIDENCE,
        telemetryId: TELEMETRY_IDS.HUB_DATA_DOCUMENTS,
        accessibilityLabelKey: I18N_KEYS.CARD_EVIDENCE_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.DATA_EVIDENCE,
        path: '/data/evidence',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'SUPPORT_DELEGATE'],
        pageId: 'page-data-evidence',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.CARD_EVIDENCE_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.CARD_EVIDENCE_TITLE,
        operationId: OPERATION_IDS.EVIDENCE_LIST,
    },
    {
        id: 'route-data-evidence-detail',
        schemaVersion: 1,
        module: MODULE_KEYS.DOCUMENTS,
        feature: FEATURE_KEYS.DATA_EVIDENCE,
        telemetryId: TELEMETRY_IDS.HUB_DATA_DOCUMENTS,
        accessibilityLabelKey: I18N_KEYS.CARD_EVIDENCE_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        routeId: ROUTE_IDS.DATA_EVIDENCE_DETAIL,
        path: '/data/evidence/:id',
        allowedPersonas: ['ORG_ADMIN', 'ORG_USER', 'SUPPORT_DELEGATE'],
        pageId: 'page-data-evidence-detail',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_DATA, I18N_KEYS.CARD_EVIDENCE_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.CARD_EVIDENCE_TITLE,
        operationId: OPERATION_IDS.EVIDENCE_GET,
    },
    // ── P2 hardening: new Admin Hub routes (PLANNED) ──────────────────────────
    // Organization admin — missing capabilities
    {
        id: 'route-org-approval-policies',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_APPROVAL_POLICIES,
        telemetryId: TELEMETRY_IDS.ADMIN_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_APPROVAL_POLICIES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        routeId: ROUTE_IDS.ORGANIZATION_APPROVAL_POLICIES_LIST,
        path: '/admin/approval-policies',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-org-approval-policies',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_APPROVAL_POLICIES_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_APPROVAL_POLICIES_TITLE,
        operationId: OPERATION_IDS.ORGANIZATION_APPROVAL_POLICIES_LIST,
    },
    {
        id: 'route-org-approval-policies-detail',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_APPROVAL_POLICIES,
        telemetryId: TELEMETRY_IDS.ADMIN_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_APPROVAL_POLICIES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        routeId: ROUTE_IDS.ORGANIZATION_APPROVAL_POLICIES_DETAIL,
        path: '/admin/approval-policies/:id',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-org-approval-policies-detail',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_APPROVAL_POLICIES_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_APPROVAL_POLICIES_TITLE,
        operationId: OPERATION_IDS.ORGANIZATION_APPROVAL_POLICIES_LIST,
    },
    {
        id: 'route-org-permission-bundles',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_PERMISSION_BUNDLES,
        telemetryId: TELEMETRY_IDS.ADMIN_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_PERMISSION_BUNDLES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        routeId: ROUTE_IDS.ORGANIZATION_PERMISSION_BUNDLES_LIST,
        path: '/admin/permission-bundles',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-org-permission-bundles',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_PERMISSION_BUNDLES_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_PERMISSION_BUNDLES_TITLE,
        operationId: OPERATION_IDS.ORGANIZATION_PERMISSION_BUNDLES_LIST,
    },
    {
        id: 'route-org-permission-bundles-detail',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_PERMISSION_BUNDLES,
        telemetryId: TELEMETRY_IDS.ADMIN_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_PERMISSION_BUNDLES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        routeId: ROUTE_IDS.ORGANIZATION_PERMISSION_BUNDLES_DETAIL,
        path: '/admin/permission-bundles/:id',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-org-permission-bundles-detail',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_PERMISSION_BUNDLES_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_PERMISSION_BUNDLES_TITLE,
        operationId: OPERATION_IDS.ORGANIZATION_PERMISSION_BUNDLES_LIST,
    },
    {
        id: 'route-org-retention-policies',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_RETENTION_POLICIES,
        telemetryId: TELEMETRY_IDS.ADMIN_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_RETENTION_POLICIES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        routeId: ROUTE_IDS.ORGANIZATION_RETENTION_POLICIES,
        path: '/admin/retention-policies',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-org-retention-policies',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_RETENTION_POLICIES_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_RETENTION_POLICIES_TITLE,
        operationId: OPERATION_IDS.ORGANIZATION_RETENTION_POLICIES_READ,
    },
    {
        id: 'route-org-export-policies',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_EXPORT_POLICIES,
        telemetryId: TELEMETRY_IDS.ADMIN_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_EXPORT_POLICIES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        routeId: ROUTE_IDS.ORGANIZATION_EXPORT_POLICIES,
        path: '/admin/export-policies',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-org-export-policies',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_EXPORT_POLICIES_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_EXPORT_POLICIES_TITLE,
        operationId: OPERATION_IDS.ORGANIZATION_EXPORT_POLICIES_READ,
    },
    {
        id: 'route-org-security-settings',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_SECURITY_SETTINGS,
        telemetryId: TELEMETRY_IDS.ADMIN_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_SECURITY_SETTINGS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        routeId: ROUTE_IDS.ORGANIZATION_SECURITY_SETTINGS,
        path: '/admin/security-settings',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-org-security-settings',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_SECURITY_SETTINGS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_SECURITY_SETTINGS_TITLE,
        operationId: OPERATION_IDS.ORGANIZATION_SECURITY_SETTINGS_READ,
    },
    {
        id: 'route-org-regulatory-profile',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_REGULATORY_PROFILE,
        telemetryId: TELEMETRY_IDS.ADMIN_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_REGULATORY_PROFILE_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        routeId: ROUTE_IDS.ORGANIZATION_REGULATORY_PROFILE,
        path: '/admin/regulatory-profile',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-org-regulatory-profile',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_REGULATORY_PROFILE_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_REGULATORY_PROFILE_TITLE,
        operationId: OPERATION_IDS.ORGANIZATION_REGULATORY_PROFILE_READ,
    },
    {
        id: 'route-org-jurisdiction-settings',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_JURISDICTION_SETTINGS,
        telemetryId: TELEMETRY_IDS.ADMIN_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_JURISDICTION_SETTINGS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        routeId: ROUTE_IDS.ORGANIZATION_JURISDICTION_SETTINGS,
        path: '/admin/jurisdiction-settings',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-org-jurisdiction-settings',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_JURISDICTION_SETTINGS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_JURISDICTION_SETTINGS_TITLE,
        operationId: OPERATION_IDS.ORGANIZATION_JURISDICTION_SETTINGS_READ,
    },
    {
        id: 'route-org-support-access',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_SUPPORT_ACCESS,
        telemetryId: TELEMETRY_IDS.ADMIN_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_SUPPORT_ACCESS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        routeId: ROUTE_IDS.ORGANIZATION_SUPPORT_ACCESS,
        path: '/admin/support-access',
        allowedPersonas: ['ORG_ADMIN'],
        pageId: 'page-org-support-access',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_SUPPORT_ACCESS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_SUPPORT_ACCESS_TITLE,
        operationId: OPERATION_IDS.ORGANIZATION_SUPPORT_ACCESS_READ,
    },
    // Platform admin — missing capabilities
    {
        id: 'route-platform-security-posture',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_SECURITY_POSTURE,
        telemetryId: TELEMETRY_IDS.ADMIN_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_SECURITY_POSTURE_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        routeId: ROUTE_IDS.PLATFORM_SECURITY_POSTURE,
        path: '/admin/platform/security-posture',
        allowedPersonas: ['PLATFORM_ROOT', 'PLATFORM_ADMIN'],
        pageId: 'page-platform-security-posture',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_SECURITY_POSTURE_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_SECURITY_POSTURE_TITLE,
        operationId: OPERATION_IDS.PLATFORM_SECURITY_POSTURE_READ,
    },
    {
        id: 'route-platform-data-provider-health',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_DATA_PROVIDER_HEALTH,
        telemetryId: TELEMETRY_IDS.ADMIN_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_DATA_PROVIDER_HEALTH_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        routeId: ROUTE_IDS.PLATFORM_DATA_PROVIDER_HEALTH,
        path: '/admin/platform/data-provider-health',
        allowedPersonas: ['PLATFORM_ROOT', 'PLATFORM_ADMIN'],
        pageId: 'page-platform-data-provider-health',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_DATA_PROVIDER_HEALTH_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_DATA_PROVIDER_HEALTH_TITLE,
        operationId: OPERATION_IDS.PLATFORM_DATA_PROVIDER_HEALTH_READ,
    },
    {
        id: 'route-platform-maintenance-delegations',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_MAINTENANCE_DELEGATIONS,
        telemetryId: TELEMETRY_IDS.ADMIN_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_MAINTENANCE_DELEGATIONS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        routeId: ROUTE_IDS.PLATFORM_MAINTENANCE_DELEGATIONS_LIST,
        path: '/admin/platform/maintenance-delegations',
        allowedPersonas: ['PLATFORM_ROOT', 'PLATFORM_ADMIN'],
        pageId: 'page-platform-maintenance-delegations',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_MAINTENANCE_DELEGATIONS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_MAINTENANCE_DELEGATIONS_TITLE,
        operationId: OPERATION_IDS.PLATFORM_MAINTENANCE_DELEGATIONS_LIST,
    },
    {
        id: 'route-platform-maintenance-delegations-create',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_MAINTENANCE_DELEGATIONS,
        telemetryId: TELEMETRY_IDS.ADMIN_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_MAINTENANCE_DELEGATIONS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        routeId: ROUTE_IDS.PLATFORM_MAINTENANCE_DELEGATIONS_CREATE,
        path: '/admin/platform/maintenance-delegations/create',
        allowedPersonas: ['PLATFORM_ROOT', 'PLATFORM_ADMIN'],
        pageId: 'page-platform-maintenance-delegations-create',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_MAINTENANCE_DELEGATIONS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_MAINTENANCE_DELEGATIONS_TITLE,
        operationId: OPERATION_IDS.PLATFORM_MAINTENANCE_DELEGATIONS_CREATE,
    },
    {
        id: 'route-platform-maintenance-delegations-detail',
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_MAINTENANCE_DELEGATIONS,
        telemetryId: TELEMETRY_IDS.ADMIN_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_MAINTENANCE_DELEGATIONS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        routeId: ROUTE_IDS.PLATFORM_MAINTENANCE_DELEGATIONS_DETAIL,
        path: '/admin/platform/maintenance-delegations/:id',
        allowedPersonas: ['PLATFORM_ROOT', 'PLATFORM_ADMIN'],
        pageId: 'page-platform-maintenance-delegations-detail',
        layoutId: 'app',
        authentication: 'authenticated',
        availability: 'AVAILABLE',
        breadcrumbKeys: [I18N_KEYS.NAVIGATION_ADMIN, I18N_KEYS.PAGE_MAINTENANCE_DELEGATIONS_TITLE],
        navigationPlacement: 'hidden',
        titleKey: I18N_KEYS.PAGE_MAINTENANCE_DELEGATIONS_TITLE,
        operationId: OPERATION_IDS.PLATFORM_MAINTENANCE_DELEGATIONS_READ,
    },
];
/** Route lookup helpers. */
export function getRouteById(routeId) {
    return canonicalRouteRegistry.find((r) => r.routeId === routeId);
}
export function getRoutesByLayout(layoutId) {
    return canonicalRouteRegistry.filter((r) => r.layoutId === layoutId);
}
export function getRouteByPath(path) {
    const exact = canonicalRouteRegistry.find((r) => r.path === path);
    if (exact)
        return exact;
    for (const r of canonicalRouteRegistry) {
        if (r.path.includes('/:')) {
            const pattern = r.path.replace(/:[^/]+/g, '[^/]+');
            const re = new RegExp('^' + pattern + '$');
            if (re.test(path))
                return r;
        }
    }
    return undefined;
}
export function getNavigationRoutes() {
    return canonicalRouteRegistry.filter((r) => r.navigationPlacement !== 'hidden' &&
        r.authentication !== 'public' &&
        r.availability === 'AVAILABLE');
}
export function isPublicRoute(routeId) {
    const route = getRouteById(routeId);
    return route?.authentication === 'public';
}
export function getRouteIds() {
    return canonicalRouteRegistry.map((r) => r.routeId);
}
